import { Button, message, Select, Skeleton, Tag, Modal } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useDropzone } from "react-dropzone"
import { FaUpload } from "react-icons/fa"
import styled from "styled-components"
import JSZip from "jszip"
import { cloneDeep } from "lodash"
import parseSd3, {
  ISd3Validation,
  ISwimDatum,
  validateSd3,
} from "../utils/parseSd3"
import parseHy3Entries from "../utils/parseHy3Entries"
import { SD3_AGE_GROUP_TO_DB } from "../utils/ageGroupConstants"
// Import directly (not the "../utils" barrel) to avoid a circular-import
// ordering issue that leaves these undefined under CJS/Jest.
import { gen, swimminglyApi } from "../utils/helperFunctions"
import { CustomEventEmitter, IClub } from "../utils/sharedDataTypes"
import { useNoRenderRef } from "../utils/customHooks"
import { IClubSeasonInformation } from "../MeetActionsModal"
import { IManageRosterSeasonSwimmer } from "../ManageRoster"
import {
  parseHy3Results,
  findUniqueHy3Swimmers,
} from "../utils/parseHy3Results"
import type { IHy3ParseOptions } from "../utils/parseHy3Results"

const Sd3Manager = new CustomEventEmitter()

interface IImportEntriesModalProps {
  mode?: "entries" | "results"
  visible: boolean
  setVisible: (newVisible: boolean) => void
  meetId: number
  refresh: () => void
  individualEntryLimit?: number | null
  relayEntryLimit?: number | null
  totalEntryLimit?: number | null
}

interface ISwimmerInSd3 {
  club: string
  swimmerFirstName: string
  swimmerLastName: string
  swimmerGender: string
  swimmerDOB: string
  swimmerId: number
  /** USA Swimming / HyTek national ID — populated for HY3 results imports */
  swimmerUSAId?: string
}

interface ISd3Data {
  clubNames: string[]
  swimmers: ISwimmerInSd3[]
  swimData: ISwimDatum
  meetEvents: MeetEvent[]
}

const StyledDropZone = styled.div`
  border-radius: 10px;
  border: 3px dashed var(--mediumgrey);
  cursor: pointer;
  text-align: center;
  &:hover {
    border: 3px dashed lightblue;
    color: var(--primaryblue);
  }
`

type SetSd3 = (sd3Contents: ISd3Data) => void

type Sd3LoadStep =
  | "file-drop"
  | "hy3-options"
  | "prelims-finals-detected"
  | "session-map"
  | "file-options"
  | "zip-file-picker"
  | "club-map"
  | "no-event-numbers-warning"
  | "choose-swimmers"
  | "swimmer-exceptions"
  | "entry-limits"
  | "load-data"
  | "invalid-sd3"
  | "fix-missing-dob"
  | "summary"

function FileDropZone({
  setSd3Validation,
  setSd3Contents,
  setSd3LoadStep,
  onMultipleZipFiles,
  onHy3FileDrop,
  mode,
}: {
  setSd3Validation: (value: ISd3Validation) => void
  setSd3Contents: SetSd3
  setSd3LoadStep: (value: Sd3LoadStep) => void
  onMultipleZipFiles: ((zipFile: JSZip, validFiles: string[]) => void) | null
  onHy3FileDrop: ((contents: string) => void) | null
  mode?: "entries" | "results"
}): JSX.Element {
  const processFileContents = useCallback(
    (contents: string, ext: string) => {
      if (ext === "hy3") {
        if (onHy3FileDrop) {
          onHy3FileDrop(contents)
        } else {
          const parsedHy3 = parseHy3Entries(contents)
          setSd3Contents(parsedHy3)
          setSd3Validation({ dateOfBirthValid: true, invalidBirthdaySwimmers: [] })
          setSd3LoadStep("file-options")
        }
      } else {
        const sd3Validation = validateSd3(contents)
        setSd3Validation(sd3Validation)
        const parsedSd3 = parseSd3(contents)
        setSd3Contents(parsedSd3)
        setSd3LoadStep("file-options")
      }
    },
    [setSd3Contents, setSd3LoadStep, setSd3Validation, onHy3FileDrop],
  )

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles || acceptedFiles.length === 0) return

      const file = acceptedFiles[0]

      if (!file) {
        message.error("Problem with file")
        return
      }

      const fileNameParts = file.name.split(".")
      if (fileNameParts.length === 0) {
        message.error(
          "Invalid file extension - please upload a .sd3, .cl2, .hy3, or .zip file",
          4,
        )
        return
      }
      const extension = fileNameParts[fileNameParts.length - 1].toLowerCase()
      if (!["sd3", "cl2", "hy3", "zip"].includes(extension)) {
        message.error(
          "Invalid file extension - please upload a .sd3, .cl2, .hy3, or .zip file",
          4,
        )
        return
      }

      if (extension === "zip") {
        const zipReader = new JSZip()
        const zipFile = await zipReader.loadAsync(file)
        const validFiles: string[] = []
        zipFile.forEach((zipFilePath) => {
          if (/(cl2|sd3|hy3)$/i.test(zipFilePath)) {
            validFiles.push(zipFilePath)
          }
        })
        if (validFiles.length === 0) {
          message.error(
            "Swimmingly only supports .sd3, .cl2, and .hy3 files. Your zip file does not contain any supported files!",
            4,
          )
          return
        }
        if (validFiles.length === 1) {
          const singleFile = validFiles[0]
          const ext = singleFile.split(".").pop()?.toLowerCase() ?? ""
          const contents = await zipFile.file(singleFile)?.async("string")
          if (!contents) {
            message.error(
              "Could not read the selected file from the zip archive.",
            )
            return
          }
          processFileContents(contents, ext)
        } else {
          if (onMultipleZipFiles) {
            // Sort: hy3 first (recommended), then cl2, then sd3
            const sorted = [...validFiles].sort((a, b) => {
              const extA = a.split(".").pop()?.toLowerCase() ?? ""
              const extB = b.split(".").pop()?.toLowerCase() ?? ""
              const priority = (e: string) =>
                e === "hy3" ? 0 : e === "cl2" ? 1 : 2
              return priority(extA) - priority(extB)
            })
            onMultipleZipFiles(zipFile, sorted)
          } else {
            // results mode: just take the first valid file
            const firstFile = validFiles[0]
            const ext = firstFile.split(".").pop()?.toLowerCase() ?? ""
            const contents = await zipFile.file(firstFile)?.async("string")
            if (!contents) {
              message.error(
                "Could not read the selected file from the zip archive.",
              )
              return
            }
            processFileContents(contents, ext)
          }
        }
        return
      }

      const fileContents = await file.text()
      processFileContents(fileContents, extension)
    },
    [onMultipleZipFiles, processFileContents],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

  return (
    <React.Fragment>
      <p>You are about to import {mode === "results" ? "results" : "entries"} for your meet.</p>
      <StyledDropZone {...getRootProps()}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <React.Fragment>
            <p>Drop the files here ...</p>
            <FaUpload style={{ fontSize: "1.75rem" }} />
          </React.Fragment>
        ) : (
          <React.Fragment>
            <p>
              Drag & drop .hy3/.cl2 .zip or .hy3/.cl2/.sd3 raw file here, or click to
              upload a file!
            </p>
            <FaUpload style={{ fontSize: "1.75rem" }} />
          </React.Fragment>
        )}
      </StyledDropZone>
    </React.Fragment>
  )
}

function Hy3OptionsPanel({
  importScope,
  setImportScope,
  matchOnEventNumbers,
  setMatchOnEventNumbers,
  hasPrelimsFinals,
  scopeLocked = false,
}: {
  importScope: "finals" | "prelims" | "both"
  setImportScope: (v: "finals" | "prelims" | "both") => void
  matchOnEventNumbers: boolean
  setMatchOnEventNumbers: (v: boolean) => void
  hasPrelimsFinals: boolean
  scopeLocked?: boolean
}) {
  return (
    <React.Fragment>
      <p>
        <b>HY3 Import Options</b>
      </p>
      <p>Which rounds should be imported?</p>
      {hasPrelimsFinals && (
        <p style={{ color: "#1677ff", fontWeight: 500, marginBottom: 8 }}>
          ✓ Prelims + Finals data detected in this file.
        </p>
      )}
      {scopeLocked && (
        <p style={{ color: "#fa8c16", fontSize: 12, marginBottom: 8 }}>
          Import scope is locked based on this session's type.
        </p>
      )}
      <div style={{ marginBottom: 12 }}>
        <label style={{ marginRight: 16 }}>
          <input
            type="radio"
            name="hy3ImportScope"
            value="finals"
            checked={importScope === "finals"}
            onChange={() => setImportScope("finals")}
            disabled={scopeLocked}
            style={{ marginRight: 4 }}
          />
          Finals Only
        </label>
        {hasPrelimsFinals && (
          <label style={{ marginRight: 16 }}>
            <input
              type="radio"
              name="hy3ImportScope"
              value="both"
              checked={importScope === "both"}
              onChange={() => setImportScope("both")}
              disabled={scopeLocked}
              style={{ marginRight: 4 }}
            />
            Prelims + Finals (creates two sessions)
          </label>
        )}
        {!hasPrelimsFinals && (
          <label>
            <input
              type="radio"
              name="hy3ImportScope"
              value="both"
              checked={importScope === "both"}
              onChange={() => setImportScope("both")}
              disabled={scopeLocked}
              style={{ marginRight: 4 }}
            />
            Finals + Prelims
          </label>
        )}
        {!hasPrelimsFinals && (
          <label style={{ marginLeft: 16 }}>
            <input
              type="radio"
              name="hy3ImportScope"
              value="prelims"
              checked={importScope === "prelims"}
              onChange={() => setImportScope("prelims")}
              disabled={scopeLocked}
              style={{ marginRight: 4 }}
            />
            Prelims Only
          </label>
        )}
      </div>
      <div>
        <label>
          <input
            type="checkbox"
            checked={matchOnEventNumbers}
            onChange={(e) => setMatchOnEventNumbers(e.target.checked)}
            style={{ marginRight: 4 }}
          />
          Match on Event Numbers
        </label>
        <p style={{ fontSize: "0.85em", color: "#666", marginTop: 4 }}>
          When checked, events are matched by their HY3 event number (requires
          event numbers to match between the file and this meet). When unchecked,
          events are matched by distance, stroke, gender, and age group instead.
        </p>
      </div>
    </React.Fragment>
  )
}

function FileOptionsPanel({
  matchOnEventNumbers,
  setMatchOnEventNumbers,
}: {
  matchOnEventNumbers: boolean
  setMatchOnEventNumbers: (v: boolean) => void
}) {
  return (
    <React.Fragment>
      <p>
        <b>Import Options</b>
      </p>
      <div>
        <label>
          <input
            type="checkbox"
            checked={matchOnEventNumbers}
            onChange={(e) => setMatchOnEventNumbers(e.target.checked)}
            style={{ marginRight: 4 }}
          />
          Match on Event Numbers
        </label>
        <p style={{ fontSize: "0.85em", color: "#666", marginTop: 4 }}>
          When checked, events are matched by their event number in addition to
          distance, stroke, gender, and age group. When unchecked, only distance,
          stroke, gender, and age group are used for matching.
        </p>
      </div>
    </React.Fragment>
  )
}

const StyledClubMappingDiv = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 10px;
  align-items: center;
  p {
    padding: 0;
    margin: 0;
  }
`

const ClubMapTable = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  align-items: center;
  column-gap: 12px;
  row-gap: 8px;
`

// ── Fuzzy-match helpers ────────────────────────────────────────────────────

function normalizeClubName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "")
}

function levenshtein(a: string, b: string): number {
  const m = a.length
  const n = b.length
  const dp: number[] = Array.from({ length: n + 1 }, (_, j) => j)
  for (let i = 1; i <= m; i++) {
    let prev = dp[0]
    dp[0] = i
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j]
      dp[j] =
        a[i - 1] === b[j - 1]
          ? prev
          : 1 + Math.min(prev, dp[j], dp[j - 1])
      prev = tmp
    }
  }
  return dp[n]
}

function clubSimilarity(fileClub: string, dbClub: string): number {
  const a = normalizeClubName(fileClub)
  const b = normalizeClubName(dbClub)
  if (!a || !b) return 0
  if (a === b) return 1
  if (a.includes(b) || b.includes(a)) return 0.9
  const dist = levenshtein(a, b)
  return Math.max(0, 1 - dist / Math.max(a.length, b.length))
}

function bestClubMatch(
  fileClub: string,
  meetClubs: IClub[],
): { club: IClub; score: number } | null {
  let best: { club: IClub; score: number } | null = null
  for (const c of meetClubs) {
    const score = clubSimilarity(fileClub, c.nameLong ?? "")
    if (!best || score > best.score) best = { club: c, score }
  }
  return best && best.score >= 0.5 ? best : null
}

function confidenceBadge(score: number): { label: string; color: string } {
  if (score >= 0.95) return { label: "Exact match", color: "green" }
  if (score >= 0.7) return { label: "Good match", color: "blue" }
  return { label: "Possible match", color: "orange" }
}

function MapClubs({
  clubMapping,
  setClubMapping,
  clubNames,
  meetClubs,
}: {
  clubMapping: { [key: string]: { clubId: number; clubName: string } }
  setClubMapping: (newMapping: {
    [key: string]: { clubId: number; clubName: string }
  }) => void
  clubNames: string[]
  meetClubs: IClub[]
}) {
  // Track suggested scores (computed once on mount) for confidence badges
  const [suggestedScores] = React.useState<Record<string, number>>(() => {
    const scores: Record<string, number> = {}
    for (const name of clubNames) {
      const match = bestClubMatch(name, meetClubs)
      if (match) scores[name] = match.score
    }
    return scores
  })

  // Re-run when meetClubs arrives — guards against race where meetClubs loads after MapClubs mounts
  React.useEffect(() => {
    if (meetClubs.length === 0) return
    const additions: typeof clubMapping = {}
    for (const name of clubNames) {
      if (clubMapping[name]) continue
      const match = bestClubMatch(name, meetClubs)
      if (match) {
        additions[name] = { clubId: match.club.clubId ?? -1, clubName: match.club.nameLong ?? "" }
      }
    }
    if (Object.keys(additions).length > 0) {
      setClubMapping({ ...clubMapping, ...additions })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetClubs])

  const updateClubMapping = (clubFromFile: string, sqlClubId: number) => {
    const newMapping = cloneDeep(clubMapping)
    if (sqlClubId === -2) {
      newMapping[clubFromFile] = { clubId: -2, clubName: "do not import" }
    } else {
      for (const key of Object.keys(newMapping)) {
        if (newMapping[key]?.clubId === sqlClubId && key !== clubFromFile) {
          delete newMapping[key]
        }
      }
      newMapping[clubFromFile] = {
        clubId: sqlClubId,
        clubName: meetClubs.find((c) => c.clubId === sqlClubId)?.nameLong ?? "",
      }
    }
    setClubMapping(newMapping)
  }

  const selectOptions = [
    { value: -2, label: "Do NOT import this club" },
    ...meetClubs.map((c) => ({ value: c.clubId ?? -1, label: c.nameLong ?? "" })),
  ]

  return (
    <React.Fragment>
      <p style={{ marginBottom: 12 }}>
        Match each club in your file to its Swimmingly team. Suggestions are
        pre-filled — confirm or change as needed.
      </p>
      <ClubMapTable>
        <span style={{ fontWeight: 600, fontSize: 13, color: "#595959" }}>Club in file</span>
        <span style={{ fontWeight: 600, fontSize: 13, color: "#595959" }}>Swimmingly team</span>
        <span />
        {clubNames.map((clubName, idx) => {
          const currentId = clubMapping[clubName]?.clubId
          const score = suggestedScores[clubName]
          const badge = score != null ? confidenceBadge(score) : null
          return (
            <React.Fragment key={`club_row_${idx}`}>
              <span style={{ fontWeight: 500 }}>{clubName}</span>
              <Select
                style={{ width: "100%" }}
                placeholder="Search or choose a team…"
                value={currentId ?? undefined}
                onChange={(val: number) => updateClubMapping(clubName, val)}
                options={selectOptions}
                showSearch={{
                  filterOption: (input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase()),
                }}
              />
              {badge && currentId != null && currentId !== -2 ? (
                <Tag color={badge.color} style={{ margin: 0 }}>
                  {badge.label}
                </Tag>
              ) : (
                <span />
              )}
            </React.Fragment>
          )
        })}
      </ClubMapTable>
    </React.Fragment>
  )
}

function swimmerMatchScore(sd3Sw: ISwimmerInSd3, rosterSw: IManageRosterSeasonSwimmer): number {
  const sd3Gender = sd3Sw.swimmerGender.toLowerCase() === "f" ? "Girl" : "Boy"
  if (sd3Gender !== rosterSw.gender) return 0
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z]/g, "")
  const a1 = norm(sd3Sw.swimmerLastName), b1 = norm(rosterSw.lastName || "")
  const a2 = norm(sd3Sw.swimmerFirstName), b2 = norm(rosterSw.firstName || "")
  const lastSim = a1 && b1 ? 1 - levenshtein(a1, b1) / Math.max(a1.length, b1.length) : 0
  const firstSim = a2 && b2 ? 1 - levenshtein(a2, b2) / Math.max(a2.length, b2.length) : 0
  const dobBonus = sd3Sw.swimmerDOB === rosterSw.dateOfBirth ? 0.3 : 0
  return Math.min(1, lastSim * 0.45 + firstSim * 0.25 + dobBonus)
}

function ChooseSwimmers({
  meetId,
  sd3Contents,
  setSd3Contents,
  setSd3LoadStep,
  clubMapping,
  autoAdvanceStep,
  meetClubs,
  mode,
}: {
  meetId: number
  sd3Contents: ISd3Data
  setSd3Contents: SetSd3
  setSd3LoadStep: (newStep: Sd3LoadStep) => void
  clubMapping: { [key: string]: { clubId: number; clubName: string } }
  autoAdvanceStep: "entry-limits" | "load-data"
  meetClubs: IClub[]
  mode?: "entries" | "results"
}) {
  const [clubSeasons, setClubSeasons] = useState<IClubSeasonInformation[]>([])
  const [sd3SwimmersWhoNeedMatches, setSd3SwimmersWhoNeedMatches] = useState<
    ISwimmerInSd3[]
  >([])
  const [unmatchedRosterSwimmers, setUnmatchedRosterSwimmers] = useState<
    { swimmer: IManageRosterSeasonSwimmer; clubId: number; clubName: string }[]
  >([])
  const [sd3ToRosterSwimmerMapping, setSd3ToRosterSwimmerMapping] = useState<
    ISwimmerInSd3[]
  >([])
  const [suggestions, setSuggestions] = useState<Record<string, { swimmerId: number; score: number }>>({})
  const [autoMatchExpanded, setAutoMatchExpanded] = useState(false)
  const [allRosterSwimmers, setAllRosterSwimmers] = useState<
    { swimmer: IManageRosterSeasonSwimmer; clubId: number; clubName: string }[]
  >([])
  const noRenderSd3Contents = useNoRenderRef<ISd3Data>(sd3Contents)

  function isPayingClub(sd3Club: string): boolean {
    const clubId = clubMapping[sd3Club]?.clubId
    if (!clubId) return false
    return meetClubs.find((mc) => mc.clubId === clubId)?.isSwimminglyCustomer === 1
  }

  function swKey(sw: ISwimmerInSd3): string {
    return `${sw.swimmerFirstName.toLowerCase()}|${sw.swimmerLastName.toLowerCase()}|${sw.swimmerDOB}|${sw.swimmerGender}|${sw.club}`
  }

  function getMappedId(sw: ISwimmerInSd3): number {
    return sd3ToRosterSwimmerMapping.find(
      (m) =>
        m.swimmerFirstName.toLowerCase() === sw.swimmerFirstName.toLowerCase() &&
        m.swimmerLastName.toLowerCase() === sw.swimmerLastName.toLowerCase() &&
        m.swimmerDOB === sw.swimmerDOB &&
        m.swimmerGender === sw.swimmerGender &&
        m.club === sw.club,
    )?.swimmerId ?? 0
  }

  useEffect(() => {
    let mounted = true
    Sd3Manager.on("saveSwimmerMapping", () => {
      const newSd3Contents = cloneDeep(sd3Contents)
      for (const swimmerToMap of sd3ToRosterSwimmerMapping) {
        const sd3Swimmer = newSd3Contents.swimmers.find(
          (sw) =>
            sw.swimmerFirstName.toLowerCase() ===
              swimmerToMap.swimmerFirstName.toLowerCase() &&
            sw.swimmerLastName.toLowerCase() ===
              swimmerToMap.swimmerLastName.toLowerCase() &&
            sw.swimmerDOB === swimmerToMap.swimmerDOB &&
            sw.swimmerGender === swimmerToMap.swimmerGender &&
            sw.club === swimmerToMap.club,
        )
        if (sd3Swimmer) {
          if (swimmerToMap.swimmerId && swimmerToMap.swimmerId > 0) {
            // Explicit roster match (manual or auto)
            sd3Swimmer.swimmerId = swimmerToMap.swimmerId
          } else if (swimmerToMap.swimmerId === -1) {
            // Explicit "add as new swimmer" override — clears any prior auto-match
            sd3Swimmer.swimmerId = -1
          }
        }
      }
      if (mounted) {
        setSd3Contents(newSd3Contents)
        // Only require DOB entry for swimmers that are still unmatched — those will trigger an INSERT
        const unmatchedNoDob = newSd3Contents.swimmers.filter(
          (sw) =>
            (!sw.swimmerId || sw.swimmerId <= 0) &&
            (!sw.swimmerDOB || sw.swimmerDOB === "" || sw.swimmerDOB.startsWith("age-")),
        )
        if (unmatchedNoDob.length > 0) {
          setSd3LoadStep("fix-missing-dob")
        } else {
          setSd3LoadStep(autoAdvanceStep)
        }
      }
    })
    return () => {
      Sd3Manager.removeAllListeners("saveSwimmerMapping")
      mounted = false
    }
  }, [sd3Contents, sd3ToRosterSwimmerMapping, setSd3Contents, setSd3LoadStep, autoAdvanceStep])

  useEffect(() => {
    if (meetId && meetId !== -1) {
      swimminglyApi
        .get(gen(`/api/getMeetInformation/${meetId}`))
        .then((data) => {
          if (data.status === "success") {
            setClubSeasons(data.clubSeasons)
          }
        })
    }
  }, [meetId])

  useEffect(() => {
    const newSd3Contents = cloneDeep(noRenderSd3Contents.current)
    const swimmersFromSd3 = newSd3Contents.swimmers
    const updateSwimmers: Promise<void>[] = []
    const newUnmatchedRosterSwimmers: {
      swimmer: IManageRosterSeasonSwimmer
      clubId: number
      clubName: string
    }[] = []
    const newAllRosterSwimmers: {
      swimmer: IManageRosterSeasonSwimmer
      clubId: number
      clubName: string
    }[] = []
    // loop through the clubs in the meet, pull in their rosters, and try to match to swimmers in the sd3
    for (let i = 0; i < clubSeasons.length; i++) {
      const clubSeason = clubSeasons[i]
      const updateClubSeasonSwimmers = swimminglyApi
        .get(gen(`/api/getRosterForClubSeason/${clubSeason.clubSeasonId}`))
        .then(
          (data: {
            status: string
            swimmers: IManageRosterSeasonSwimmer[]
          }) => {
            // at this point we've pulled roster data for the ith clubSeason. now let's search the swimmers from our
            // sd3 to see if they match any of the swimmers we pulled from the clubhouse roster
            if (!data.swimmers) return
            for (let j = 0; j < data.swimmers.length; j++) {
              const rosterSwimmer = data.swimmers[j]
              const rosterFirstName = (rosterSwimmer.firstName || "").trim().toLowerCase()
              const rosterLastName = (rosterSwimmer.lastName || "").trim().toLowerCase()
              const sd3Swimmer =
                // First try national ID match (populated for HY3 results imports)
                (rosterSwimmer.usaSwimmingId
                  ? swimmersFromSd3.find((sw) => {
                      if (clubMapping[sw.club]?.clubId !== clubSeason.club.clubId)
                        return false
                      return (
                        sw.swimmerUSAId &&
                        sw.swimmerUSAId === rosterSwimmer.usaSwimmingId
                      )
                    })
                  : undefined) ??
                // Fall back to name + DOB match
                swimmersFromSd3.find((sw) => {
                  // first filter the sd3 swimmers down to only those who are on the club we're looking at
                  if (clubMapping[sw.club]?.clubId !== clubSeason.club.clubId)
                    return false
                  const swimmerGender =
                    sw.swimmerGender.toLowerCase() === "f" ? "Girl" : "Boy"
                  // trim to handle SD3 files with spaces before commas (e.g. "Peterson , Benjamin")
                  return (
                    (sw.swimmerFirstName.trim().toLowerCase() === rosterFirstName ||
                      sw.swimmerFirstName.trim().split(" ")[0].toLowerCase() === rosterFirstName) &&
                    sw.swimmerLastName.trim().toLowerCase() === rosterLastName &&
                    sw.swimmerDOB === rosterSwimmer.dateOfBirth &&
                    swimmerGender === rosterSwimmer.gender
                  )
                }) ??
                // Final fallback: name + gender only when the file swimmer has no valid DOB
                swimmersFromSd3.find((sw) => {
                  if (clubMapping[sw.club]?.clubId !== clubSeason.club.clubId)
                    return false
                  const dobMissing = !sw.swimmerDOB || sw.swimmerDOB === "" || sw.swimmerDOB.startsWith("age-")
                  if (!dobMissing) return false
                  const swimmerGender =
                    sw.swimmerGender.toLowerCase() === "f" ? "Girl" : "Boy"
                  return (
                    (sw.swimmerFirstName.trim().toLowerCase() === rosterFirstName ||
                      sw.swimmerFirstName.trim().split(" ")[0].toLowerCase() === rosterFirstName) &&
                    sw.swimmerLastName.trim().toLowerCase() === rosterLastName &&
                    swimmerGender === rosterSwimmer.gender
                  )
                })
              if (sd3Swimmer) {
                sd3Swimmer.swimmerId = rosterSwimmer.swimmerId
              } else {
                newUnmatchedRosterSwimmers.push({
                  swimmer: rosterSwimmer,
                  clubId: clubSeason.club.clubId || -1,
                  clubName: clubSeason.club.nameLong || "",
                })
              }
              // Track all roster swimmers (matched + unmatched) for auto-match display
              newAllRosterSwimmers.push({
                swimmer: rosterSwimmer,
                clubId: clubSeason.club.clubId || -1,
                clubName: clubSeason.club.nameLong || "",
              })
            }
          },
        )
      updateSwimmers.push(updateClubSeasonSwimmers)
    }
    Promise.all(updateSwimmers).then(() => {
      // Propagate swimmerId to same-person duplicates (e.g. "Owen J" and "Owen 'James'" in the same file)
      for (const sw of newSd3Contents.swimmers) {
        if (sw.swimmerId && sw.swimmerId > 0) continue
        const clubId = clubMapping[sw.club]?.clubId
        const matched = newSd3Contents.swimmers.find(
          (other) =>
            other !== sw &&
            other.swimmerId != null && other.swimmerId > 0 &&
            other.swimmerLastName.trim().toLowerCase() === sw.swimmerLastName.trim().toLowerCase() &&
            other.swimmerDOB === sw.swimmerDOB &&
            other.swimmerGender === sw.swimmerGender &&
            clubMapping[other.club]?.clubId === clubId &&
            // Don't merge HY3 swimmers who have distinct USA IDs — they're different people
            !(sw.swimmerUSAId && other.swimmerUSAId && sw.swimmerUSAId !== other.swimmerUSAId),
        )
        if (matched) sw.swimmerId = matched.swimmerId
      }
      const swimmersWithoutRosterMatches: ISwimmerInSd3[] =
        newSd3Contents.swimmers.filter(
          (sw) => !sw.swimmerId || sw.swimmerId <= 0,
        )
      setSd3Contents(newSd3Contents)
      setSd3SwimmersWhoNeedMatches(swimmersWithoutRosterMatches)
      setUnmatchedRosterSwimmers(newUnmatchedRosterSwimmers)
      setAllRosterSwimmers(newAllRosterSwimmers)
      if (swimmersWithoutRosterMatches.length === 0) {
        setSd3LoadStep(autoAdvanceStep)
      }
    })
  }, [
    noRenderSd3Contents,
    clubMapping,
    setSd3Contents,
    clubSeasons,
    setSd3LoadStep,
  ])

  // Greedy bipartite suggestion matching: score every unmatched sd3↔roster pair, assign highest-scoring non-conflicting pairs
  useEffect(() => {
    if (sd3SwimmersWhoNeedMatches.length === 0 || unmatchedRosterSwimmers.length === 0) return
    type ScoredPair = { sd3Sw: ISwimmerInSd3; rosterSwimmerId: number; score: number }
    const pairs: ScoredPair[] = []
    for (const sd3Sw of sd3SwimmersWhoNeedMatches) {
      const clubId = clubMapping[sd3Sw.club]?.clubId
      for (const c of unmatchedRosterSwimmers.filter((r) => r.clubId === clubId)) {
        const score = swimmerMatchScore(sd3Sw, c.swimmer)
        if (score >= 0.45) pairs.push({ sd3Sw, rosterSwimmerId: c.swimmer.swimmerId, score })
      }
    }
    pairs.sort((a, b) => b.score - a.score)
    const assignedSd3 = new Set<string>()
    const assignedRoster = new Set<number>()
    const newSuggestions: Record<string, { swimmerId: number; score: number }> = {}
    for (const pair of pairs) {
      const key = swKey(pair.sd3Sw)
      if (assignedSd3.has(key) || assignedRoster.has(pair.rosterSwimmerId)) continue
      assignedSd3.add(key)
      assignedRoster.add(pair.rosterSwimmerId)
      newSuggestions[key] = { swimmerId: pair.rosterSwimmerId, score: pair.score }
    }
    setSuggestions(newSuggestions)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sd3SwimmersWhoNeedMatches, unmatchedRosterSwimmers])

  const updateSwimmerMapping = useCallback(
    (swimmerInSd3: ISwimmerInSd3, sqlId: number) => {
      const withoutThisSwimmer = (arr: ISwimmerInSd3[]) =>
        arr.filter(
          (swimmer) =>
            swimmer.swimmerFirstName.toLowerCase() !==
              swimmerInSd3.swimmerFirstName.toLowerCase() ||
            swimmer.swimmerLastName.toLowerCase() !==
              swimmerInSd3.swimmerLastName.toLowerCase() ||
            swimmer.swimmerDOB !== swimmerInSd3.swimmerDOB ||
            swimmer.swimmerGender !== swimmerInSd3.swimmerGender ||
            swimmer.club !== swimmerInSd3.club,
        )
      if (sqlId === -1) {
        // Explicit "new swimmer" override — store -1 so the auto-match display tracks it
        const next = withoutThisSwimmer(sd3ToRosterSwimmerMapping)
        next.push({ ...swimmerInSd3, swimmerId: -1 })
        setSd3ToRosterSwimmerMapping(next)
        return
      }
      if (!sqlId || sqlId <= 0) {
        setSd3ToRosterSwimmerMapping(withoutThisSwimmer(sd3ToRosterSwimmerMapping))
        return
      }
      // get rid of all swimmers that we've already mapped to the supplied sqlId
      const newSd3ToRosterSwimmerMapping = cloneDeep(
        sd3ToRosterSwimmerMapping,
      ).filter((swimmer) => swimmer.swimmerId !== sqlId)
      // now check whether the swimmer we've received input for is already in our swimmer mapping
      const swimmer = newSd3ToRosterSwimmerMapping.find(
        (sw) =>
          sw.swimmerFirstName.toLowerCase() ===
            swimmerInSd3.swimmerFirstName.toLowerCase() &&
          sw.swimmerLastName.toLowerCase() ===
            swimmerInSd3.swimmerLastName.toLowerCase() &&
          sw.swimmerDOB === swimmerInSd3.swimmerDOB &&
          sw.swimmerGender === swimmerInSd3.swimmerGender &&
          sw.club === swimmerInSd3.club,
      )
      if (swimmer) {
        // update the swimmer because they were already in the mapping
        swimmer.swimmerId = sqlId
      } else {
        // add the swimmer to the mapping because they weren't there
        newSd3ToRosterSwimmerMapping.push({ ...swimmerInSd3, swimmerId: sqlId })
      }
      setSd3ToRosterSwimmerMapping(newSd3ToRosterSwimmerMapping)
    },
    [sd3ToRosterSwimmerMapping, setSd3ToRosterSwimmerMapping],
  )

  const needMatchKeys = new Set(sd3SwimmersWhoNeedMatches.map((sw) => swKey(sw)))
  const autoMatchedSwimmers = sd3Contents.swimmers.filter(
    (sw) => !!sw.swimmerId && sw.swimmerId > 0 && !needMatchKeys.has(swKey(sw)),
  )

  const mappedCount = sd3SwimmersWhoNeedMatches.filter((sw) => getMappedId(sw) > 0).length
  const totalNeedMatches = sd3SwimmersWhoNeedMatches.length
  const unacceptedSuggestionCount = sd3SwimmersWhoNeedMatches.filter(
    (sw) => !!suggestions[swKey(sw)] && getMappedId(sw) <= 0,
  ).length

  const acceptAllSuggestions = () => {
    const usedRosterIds = new Set<number>(
      sd3ToRosterSwimmerMapping.filter((m) => m.swimmerId > 0).map((m) => m.swimmerId),
    )
    const newMapping = cloneDeep(sd3ToRosterSwimmerMapping).filter((m) => m.swimmerId > 0)
    for (const sd3Sw of sd3SwimmersWhoNeedMatches) {
      const key = swKey(sd3Sw)
      const suggestion = suggestions[key]
      if (!suggestion || usedRosterIds.has(suggestion.swimmerId) || getMappedId(sd3Sw) > 0) continue
      newMapping.push({ ...sd3Sw, swimmerId: suggestion.swimmerId })
      usedRosterIds.add(suggestion.swimmerId)
    }
    setSd3ToRosterSwimmerMapping(newMapping)
  }

  // Consequence label shown below each unmatched swimmer's select
  function ConsequenceBadge({ sd3Club, isCurrentlyMapped }: { sd3Club: string; isCurrentlyMapped: boolean }) {
    if (isCurrentlyMapped) return null
    const paying = isPayingClub(sd3Club)
    const thing = mode === "results" ? "result" : "entry"
    if (paying) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4, padding: "3px 7px", borderRadius: 4, background: "#fff1f0", border: "1px solid #ffa39e" }}>
          <span style={{ fontSize: 10, color: "#cf1322", fontWeight: 600 }}>⚠ Not matched:</span>
          <span style={{ fontSize: 10, color: "#cf1322" }}>{thing} excluded · swimmer not added to roster</span>
        </div>
      )
    }
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4, padding: "3px 7px", borderRadius: 4, background: "#f6ffed", border: "1px solid #b7eb8f" }}>
        <span style={{ fontSize: 10, color: "#389e0d", fontWeight: 600 }}>➕ Not matched:</span>
        <span style={{ fontSize: 10, color: "#389e0d" }}>new swimmer added to roster · {thing} included</span>
      </div>
    )
  }

  return (
    <React.Fragment>
      {/* Auto-matched accordion */}
      {autoMatchedSwimmers.length > 0 && (
        <div style={{ marginBottom: 16, border: "1px solid #b7eb8f", borderRadius: 6, overflow: "hidden" }}>
          <button
            type="button"
            onClick={() => setAutoMatchExpanded((v) => !v)}
            style={{
              width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "#f6ffed", border: "none", padding: "9px 14px", cursor: "pointer",
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 13, color: "#389e0d" }}>
              ✓ {autoMatchedSwimmers.length} swimmer{autoMatchedSwimmers.length !== 1 ? "s" : ""} auto-matched
            </span>
            <span style={{ fontSize: 11, color: "#52c41a" }}>{autoMatchExpanded ? "▲ Collapse" : "▼ Review / override"}</span>
          </button>
          {autoMatchExpanded && (
            <div style={{ padding: "10px 14px", background: "#fff" }}>
              <p style={{ fontSize: 12, color: "#595959", marginBottom: 10 }}>
                These were matched automatically. You can change the linked roster swimmer below. Only guest clubs allow removing a match entirely.
              </p>
              {autoMatchedSwimmers
                .slice()
                .sort((a, b) => a.swimmerLastName.localeCompare(b.swimmerLastName) || a.swimmerFirstName.localeCompare(b.swimmerFirstName))
                .map((sw, idx) => {
                  const currentId = getMappedId(sw)
                  // Prefer explicit override from mapping state; fall back to sd3Contents' auto-match id
                  const displayId = currentId > 0 ? currentId : (sw.swimmerId ?? -1)
                  const clubId = clubMapping[sw.club]?.clubId
                  const clubRoster = allRosterSwimmers
                    .filter((r) => r.clubId === clubId)
                    .slice()
                    .sort((a, b) => a.swimmer.lastName.localeCompare(b.swimmer.lastName) || a.swimmer.firstName.localeCompare(b.swimmer.firstName))
                  const matched = clubRoster.find((r) => r.swimmer.swimmerId === displayId)
                  const paying = isPayingClub(sw.club)
                  // Was this auto-match explicitly cleared to "new swimmer"?
                  const forcedNew = getMappedId(sw) === -1

                  return (
                    <div
                      key={`auto_${idx}`}
                      style={{
                        display: "grid", gridTemplateColumns: "1fr 20px 1fr",
                        alignItems: "center", gap: 8,
                        padding: "8px 10px", borderRadius: 6, marginBottom: 6,
                        border: `1px solid ${forcedNew ? "#ffd591" : "#b7eb8f"}`,
                        background: forcedNew ? "#fffbe6" : "#f6ffed",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{sw.swimmerFirstName} {sw.swimmerLastName}</div>
                        <div style={{ display: "flex", gap: 4, marginTop: 3, flexWrap: "wrap" as const }}>
                          <Tag color={sw.swimmerGender.toLowerCase() === "f" ? "pink" : "blue"} style={{ margin: 0, fontSize: 11, lineHeight: "16px", padding: "0 5px" }}>
                            {sw.swimmerGender.toLowerCase() === "f" ? "Girl" : "Boy"}
                          </Tag>
                          {sw.swimmerDOB && !sw.swimmerDOB.startsWith("age-") && (
                            <Tag style={{ margin: 0, fontSize: 11, lineHeight: "16px", padding: "0 5px" }}>{sw.swimmerDOB}</Tag>
                          )}
                        </div>
                      </div>
                      <span style={{ textAlign: "center" as const, color: forcedNew ? "#d9d9d9" : "#52c41a", fontSize: 14, fontWeight: 700 }}>→</span>
                      <div>
                        {!forcedNew && <div style={{ fontSize: 11, color: "#389e0d", marginBottom: 3, fontWeight: 500 }}>✓ Auto-matched</div>}
                        {forcedNew && <div style={{ fontSize: 11, color: "#d46b08", marginBottom: 3, fontWeight: 500 }}>Override: will be added as new swimmer</div>}
                        <Select
                          style={{ width: "100%" }}
                          size="small"
                          value={forcedNew ? undefined : displayId}
                          placeholder="New swimmer — will be added"
                          onChange={(val: number | undefined) => {
                            if (val && val > 0) {
                              updateSwimmerMapping(sw, val)
                            }
                          }}
                          onClear={() => {
                            if (!paying) {
                              // Guest: explicit "new swimmer" override
                              updateSwimmerMapping(sw, -1)
                            }
                          }}
                          allowClear={!paying}
                          showSearch={{
                            filterOption: (input: string, option: any) =>
                              (option?.searchLabel ?? "").toLowerCase().includes(input.toLowerCase()),
                          }}
                          optionLabelProp="label"
                        >
                          {clubRoster.map((r, rIdx) => (
                            <Select.Option
                              key={`${r.swimmer.swimmerId}_${rIdx}`}
                              value={r.swimmer.swimmerId}
                              label={`${r.swimmer.lastName}, ${r.swimmer.firstName}`}
                              searchLabel={`${r.swimmer.lastName} ${r.swimmer.firstName} ${r.swimmer.dateOfBirth}`}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontSize: 13 }}><b>{r.swimmer.lastName}</b>, {r.swimmer.firstName}</span>
                                <span style={{ color: "#999", fontSize: 11, marginLeft: 8, whiteSpace: "nowrap" as const }}>
                                  {r.swimmer.gender === "Girl" ? "F" : "M"} · {r.swimmer.dateOfBirth}
                                </span>
                              </div>
                            </Select.Option>
                          ))}
                        </Select>
                        {paying && (
                          <div style={{ fontSize: 10, color: "#8c8c8c", marginTop: 3 }}>Paying club — match cannot be cleared</div>
                        )}
                        {forcedNew && !paying && (
                          <ConsequenceBadge sd3Club={sw.club} isCurrentlyMapped={false} />
                        )}
                      </div>
                    </div>
                  )
                })}
            </div>
          )}
        </div>
      )}

      {/* Header: needs-review count + accept-all button */}
      {totalNeedMatches > 0 && (
        <div style={{ marginBottom: 16, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <p style={{ margin: 0, fontSize: 13, color: "#555", flex: 1, minWidth: 0 }}>
            These swimmers couldn't be automatically matched. Select an existing roster swimmer to link them, or leave unselected.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <Tag color={mappedCount === totalNeedMatches ? "green" : "blue"} style={{ margin: 0 }}>
              {mappedCount} / {totalNeedMatches} matched
            </Tag>
            {unacceptedSuggestionCount > 0 && (
              <Button size="small" type="primary" ghost onClick={acceptAllSuggestions}>
                Accept {unacceptedSuggestionCount} suggestion{unacceptedSuggestionCount !== 1 ? "s" : ""}
              </Button>
            )}
          </div>
        </div>
      )}

      {sd3SwimmersWhoNeedMatches
        .map((sw) => sw.club)
        .filter((v, i, a) => a.indexOf(v) === i)
        .map((sd3Club, clubIdx) => {
          const clubSwimmers = sd3SwimmersWhoNeedMatches
            .filter((sw) => sw.club === sd3Club)
            .slice()
            .sort((a, b) => a.swimmerLastName.localeCompare(b.swimmerLastName) || a.swimmerFirstName.localeCompare(b.swimmerFirstName))
          const clubRoster = unmatchedRosterSwimmers
            .filter((r) => r.clubId === clubMapping[sd3Club]?.clubId)
            .slice()
            .sort((a, b) => a.swimmer.lastName.localeCompare(b.swimmer.lastName) || a.swimmer.firstName.localeCompare(b.swimmer.firstName))
          const paying = isPayingClub(sd3Club)

          return (
            <div key={`club_${sd3Club}_${clubIdx}`} style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#1677ff" }}>
                  {clubMapping[sd3Club]?.clubName}
                </div>
                <Tag
                  color={paying ? "red" : "green"}
                  style={{ margin: 0, fontSize: 10, lineHeight: "16px", padding: "0 5px" }}
                >
                  {paying ? "Paying club" : "Guest club"}
                </Tag>
              </div>
              {/* Club-level consequence callout */}
              {paying ? (
                <div style={{ fontSize: 12, color: "#cf1322", background: "#fff1f0", border: "1px solid #ffa39e", borderRadius: 4, padding: "5px 10px", marginBottom: 8 }}>
                  ⚠ Unmatched swimmers from this club will <strong>not</strong> be added to the roster and their {mode === "results" ? "results" : "entries"} will be excluded.
                </div>
              ) : (
                <div style={{ fontSize: 12, color: "#389e0d", background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 4, padding: "5px 10px", marginBottom: 8 }}>
                  ℹ Unmatched swimmers from this club will be added as new roster swimmers and their {mode === "results" ? "results" : "entries"} will be included.
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 20px 1fr", gap: "0 8px", marginBottom: 4, padding: "0 4px" }}>
                <span style={{ fontSize: 11, color: "#999", fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.05em" }}>From Import File</span>
                <span />
                <span style={{ fontSize: 11, color: "#999", fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.05em" }}>Swimmingly Roster</span>
              </div>
              {clubSwimmers.map((sw, swIdx) => {
                const currentId = getMappedId(sw)
                const isMapped = currentId > 0
                const key = swKey(sw)
                const suggestion = suggestions[key]
                const hasPendingSuggestion = !!suggestion && !isMapped
                const borderColor = isMapped ? "#b7eb8f" : hasPendingSuggestion ? "#ffd591" : "#d9d9d9"
                const bgColor = isMapped ? "#f6ffed" : hasPendingSuggestion ? "#fffbe6" : "#fafafa"
                const suggestedRosterSwimmer = suggestion
                  ? clubRoster.find((r) => r.swimmer.swimmerId === suggestion.swimmerId)
                  : undefined

                return (
                  <div
                    key={`sw_${swIdx}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 20px 1fr",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 12px",
                      borderRadius: 6,
                      border: `1px solid ${borderColor}`,
                      background: bgColor,
                      marginBottom: 6,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {sw.swimmerFirstName} {sw.swimmerLastName}
                      </div>
                      <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" as const }}>
                        <Tag
                          color={sw.swimmerGender.toLowerCase() === "f" ? "pink" : "blue"}
                          style={{ margin: 0, fontSize: 11, lineHeight: "16px", padding: "0 5px" }}
                        >
                          {sw.swimmerGender.toLowerCase() === "f" ? "Girl" : "Boy"}
                        </Tag>
                        {sw.swimmerDOB && !sw.swimmerDOB.startsWith("age-") && (
                          <Tag style={{ margin: 0, fontSize: 11, lineHeight: "16px", padding: "0 5px" }}>
                            {sw.swimmerDOB}
                          </Tag>
                        )}
                      </div>
                    </div>

                    <span style={{ textAlign: "center" as const, color: isMapped ? "#52c41a" : "#d9d9d9", fontSize: 14, fontWeight: 700 }}>→</span>

                    <div>
                      {isMapped && (
                        <div style={{ fontSize: 11, color: "#389e0d", marginBottom: 3, fontWeight: 500 }}>✓ Matched</div>
                      )}
                      {hasPendingSuggestion && (
                        <div style={{ fontSize: 11, color: "#d46b08", marginBottom: 3, fontWeight: 500 }}>
                          {suggestion!.score >= 0.75 ? "⚡ Strong suggestion" : "💡 Suggested"}
                        </div>
                      )}
                      {!isMapped && !hasPendingSuggestion && (
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>No match found</div>
                      )}
                      <Select
                        style={{ width: "100%" }}
                        size="small"
                        value={isMapped ? currentId : undefined}
                        placeholder={
                          suggestedRosterSwimmer
                            ? `💡 ${suggestedRosterSwimmer.swimmer.lastName}, ${suggestedRosterSwimmer.swimmer.firstName}`
                            : "Select roster swimmer…"
                        }
                        onChange={(val: number | undefined) => updateSwimmerMapping(sw, val ?? -1)}
                        onClear={() => updateSwimmerMapping(sw, -1)}
                        allowClear={isMapped}
                        showSearch={{
                          filterOption: (input: string, option: any) =>
                            (option?.searchLabel ?? "").toLowerCase().includes(input.toLowerCase()),
                        }}
                        optionLabelProp="label"
                      >
                        {clubRoster.map((r, rIdx) => (
                          <Select.Option
                            key={`${r.swimmer.swimmerId}_${rIdx}`}
                            value={r.swimmer.swimmerId}
                            label={`${r.swimmer.lastName}, ${r.swimmer.firstName}`}
                            searchLabel={`${r.swimmer.lastName} ${r.swimmer.firstName} ${r.swimmer.dateOfBirth}`}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span style={{ fontSize: 13 }}>
                                <b>{r.swimmer.lastName}</b>, {r.swimmer.firstName}
                                {suggestion?.swimmerId === r.swimmer.swimmerId && (
                                  <Tag color="orange" style={{ marginLeft: 6, fontSize: 10, padding: "0 4px", lineHeight: "14px" }}>suggested</Tag>
                                )}
                              </span>
                              <span style={{ color: "#999", fontSize: 11, marginLeft: 8, whiteSpace: "nowrap" as const }}>
                                {r.swimmer.gender === "Girl" ? "F" : "M"} · {r.swimmer.dateOfBirth}
                              </span>
                            </div>
                          </Select.Option>
                        ))}
                      </Select>
                      <ConsequenceBadge sd3Club={sd3Club} isCurrentlyMapped={isMapped} />
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })}
    </React.Fragment>
  )
}

export type MeetEvent = {
  distance: string
  gender: "Boys" | "Girls" | "Mixed"
  ageGroup: string
  eventNumber: number
  stroke: string
  isRelay: 0 | 1
  course: "SCY" | "SCM" | "LCM"
  eventName: string
}

interface ISkippedEntry {
  swimmerName: string
  eventDescription: string
  reason: string
}

interface IImportSummaryClub {
  clubName: string
  individualInserted: number
  relayTeamsInserted: number
  relayLegsInserted: number
  exhibitionCount: number
  alternateACount: number
  alternateBCount: number
  alternateCCount: number
  skippedEntries: ISkippedEntry[]
}

interface IImportSummary {
  totalIndividualInserted: number
  totalRelayTeamsInserted: number
  totalRelayLegsInserted: number
  totalSkipped: number
  totalExhibition: number
  totalAlternateA: number
  totalAlternateB: number
  totalAlternateC: number
  clubs: IImportSummaryClub[]
}

interface IRelayLegReplacement {
  swimmerId: number
  swimmerFirstName: string
  swimmerLastName: string
  swimmerDOB: string
  swimmerGender: string
}

// Key format: "ind:{clubIdx}:{entryIdx}" for individual, "rel:{clubIdx}:{relayIdx}:{legIdx}" for relay legs
type EntryKey = string

function makeIndKey(clubIdx: number, entryIdx: number): EntryKey {
  return `ind:${clubIdx}:${entryIdx}`
}

function makeRelKey(clubIdx: number, relayIdx: number, legIdx: number): EntryKey {
  return `rel:${clubIdx}:${relayIdx}:${legIdx}`
}

function swimmerMatchesEntry(
  sw: ISwimmerInSd3,
  entry: { swimmerFirstName: string; swimmerLastName: string; swimmerDOB: string; swimmerGender: string },
): boolean {
  return (
    sw.swimmerFirstName.toLowerCase() === entry.swimmerFirstName.toLowerCase() &&
    sw.swimmerLastName.toLowerCase() === entry.swimmerLastName.toLowerCase() &&
    sw.swimmerDOB === entry.swimmerDOB &&
    sw.swimmerGender === entry.swimmerGender
  )
}

interface ISwimmerEntryCounts {
  individual: number  // non-exhibition, non-relay, not deselected
  relay: number       // relay leg appearances, not deselected (replacements counted for the replacement swimmer)
  total: number       // individual + relay
}

function computeAllSwimmerCounts(
  sd3Contents: ISd3Data,
  deselectedEntryKeys: Record<EntryKey, boolean>,
  relayLegReplacements: Record<EntryKey, IRelayLegReplacement>,
): Map<string, ISwimmerEntryCounts> {
  const counts = new Map<string, ISwimmerEntryCounts>()
  const clubs = sd3Contents.swimData.clubs

  // Helper to get or init counts for a swimmer within a club
  function getKey(clubName: string, sw: { swimmerFirstName: string; swimmerLastName: string; swimmerDOB: string; swimmerGender: string }) {
    return `${clubName}|${sw.swimmerFirstName.toLowerCase()}|${sw.swimmerLastName.toLowerCase()}|${sw.swimmerDOB}|${sw.swimmerGender}`
  }
  function getOrInit(key: string): ISwimmerEntryCounts {
    if (!counts.has(key)) counts.set(key, { individual: 0, relay: 0, total: 0 })
    return counts.get(key)!
  }

  clubs.forEach((club, clubIdx) => {
    // Individual entries
    club.individualEntries.forEach((entry, entryIdx) => {
      const key = makeIndKey(clubIdx, entryIdx)
      if (deselectedEntryKeys[key]) return // deselected
      if (entry.isExhibition === "1") return // exhibition doesn't count
      const swKey = getKey(club.teamFullName, entry)
      const c = getOrInit(swKey)
      c.individual += 1
      c.total += 1
    })

    // Relay legs
    club.relayEntries.forEach((relay, relayIdx) => {
      relay.relayLegs.forEach((leg, legIdx) => {
        const key = makeRelKey(clubIdx, relayIdx, legIdx)
        if (deselectedEntryKeys[key]) {
          // If there's a replacement, count it for the replacement swimmer
          const replacement = relayLegReplacements[key]
          if (replacement) {
            const repKey = getKey(club.teamFullName, {
              swimmerFirstName: replacement.swimmerFirstName,
              swimmerLastName: replacement.swimmerLastName,
              swimmerDOB: replacement.swimmerDOB,
              swimmerGender: replacement.swimmerGender,
            })
            const c = getOrInit(repKey)
            c.relay += 1
            c.total += 1
          }
          return // original swimmer is removed
        }
        const swKey = getKey(club.teamFullName, leg)
        const c = getOrInit(swKey)
        c.relay += 1
        c.total += 1
      })
    })
  })

  return counts
}

function applyEntryLimitChoices(
  sd3Contents: ISd3Data,
  deselectedEntryKeys: Record<EntryKey, boolean>,
  relayLegReplacements: Record<EntryKey, IRelayLegReplacement>,
): ISd3Data {
  const newContents = cloneDeep(sd3Contents)
  const clubs = newContents.swimData.clubs

  clubs.forEach((club, clubIdx) => {
    // Filter individual entries
    club.individualEntries = club.individualEntries.filter(
      (_, entryIdx) => !deselectedEntryKeys[makeIndKey(clubIdx, entryIdx)],
    )

    // Process relay entries: apply replacements and remove deselected legs
    club.relayEntries.forEach((relay, relayIdx) => {
      const newLegs = relay.relayLegs
        .map((leg, legIdx) => {
          const key = makeRelKey(clubIdx, relayIdx, legIdx)
          if (deselectedEntryKeys[key]) {
            const replacement = relayLegReplacements[key]
            if (replacement) {
              // Swap in replacement swimmer
              return {
                ...leg,
                swimmerFirstName: replacement.swimmerFirstName,
                swimmerLastName: replacement.swimmerLastName,
                swimmerDOB: replacement.swimmerDOB,
                swimmerGender: replacement.swimmerGender,
                swimmerUSAId: "",
              }
            }
            return null // remove this leg
          }
          return leg
        })
        .filter((leg): leg is NonNullable<typeof leg> => leg !== null)
      relay.relayLegs = newLegs
    })
  })

  return newContents
}


function sd3AgeGroupToDb(sd3Code: string): string {
  return SD3_AGE_GROUP_TO_DB[sd3Code.toUpperCase()] ?? sd3Code
}

// ─── Entry Limits Step ───────────────────────────────────────────────────────

interface IEntryLimitsStepProps {
  sd3Contents: ISd3Data
  clubMapping: { [key: string]: { clubId: number; clubName: string } }
  individualEntryLimit: number | null
  relayEntryLimit: number | null
  totalEntryLimit: number | null
  deselectedEntryKeys: Record<EntryKey, boolean>
  relayLegReplacements: Record<EntryKey, IRelayLegReplacement>
  onToggleIndividualEntry: (key: EntryKey) => void
  onToggleRelayLeg: (key: EntryKey) => void
  onOpenReplacementModal: (legKey: EntryKey) => void
}

function limitLabel(ind: number | null, relay: number | null, total: number | null): string {
  const parts: string[] = []
  if (ind !== null) parts.push(`${ind} individual`)
  if (relay !== null) parts.push(`${relay} relay`)
  if (total !== null) parts.push(`${total} total`)
  return parts.join(", ")
}

// Inject CSS keyframe animations for the entry-limits step once per page load
;(function injectEntryLimitsStyles() {
  if (typeof document === "undefined") return
  const ID = "entry-limits-step-anim"
  if (document.getElementById(ID)) return
  const el = document.createElement("style")
  el.id = ID
  el.textContent = [
    // Checkmark stroke draw
    "@keyframes elDrawCheck{from{stroke-dashoffset:50}to{stroke-dashoffset:0}}",
    // Card background transitions red → green during phase 1
    "@keyframes elResolveCard{0%{background:#fff1f0;border-color:#ffa39e}100%{background:#f6ffed;border-color:#b7eb8f}}",
    // Wrapper holds still for 45% (~500ms), then fades + slides right for 55% (~600ms)
    "@keyframes elExitRight{0%,45%{opacity:1;transform:translateX(0);max-height:800px}100%{opacity:0;transform:translateX(80px);max-height:0px}}",
  ].join("")
  document.head.appendChild(el)
})()

function EntryLimitsStep({
  sd3Contents,
  clubMapping,
  individualEntryLimit,
  relayEntryLimit,
  totalEntryLimit,
  deselectedEntryKeys,
  relayLegReplacements,
  onToggleIndividualEntry,
  onToggleRelayLeg,
  onOpenReplacementModal,
}: IEntryLimitsStepProps): JSX.Element {
  // Hooks must come before any conditional returns.

  // Swimmer keys that have ever been in violation (accumulated; never shrinks).
  // Lazy-initialized so the first render immediately knows who was violating.
  const [everViolatingKeys, setEverViolatingKeys] = useState<Set<string>>(() => {
    const counts = computeAllSwimmerCounts(sd3Contents, deselectedEntryKeys, relayLegReplacements)
    const keys = new Set<string>()
    counts.forEach((c, key) => {
      if (
        (individualEntryLimit !== null && c.individual > individualEntryLimit) ||
        (relayEntryLimit !== null && c.relay > relayEntryLimit) ||
        (totalEntryLimit !== null && c.total > totalEntryLimit)
      ) {
        keys.add(key)
      }
    })
    return keys
  })

  // Which club indices have their "Resolved" section open
  const [expandedResolvedClubs, setExpandedResolvedClubs] = useState<Set<number>>(new Set())

  // Swimmer keys currently animating out of the violating section
  const [exitingViolatingKeys, setExitingViolatingKeys] = useState<Set<string>>(new Set())

  // Tracks the violating set from the previous effect run to detect newly-resolved swimmers
  const prevViolatingRef = useRef<Set<string>>(new Set())

  // Accumulate newly-violating swimmers; detect newly-resolved and trigger exit animations
  useEffect(() => {
    const counts = computeAllSwimmerCounts(sd3Contents, deselectedEntryKeys, relayLegReplacements)

    const currentViolating = new Set<string>()
    counts.forEach((c, key) => {
      if (
        (individualEntryLimit !== null && c.individual > individualEntryLimit) ||
        (relayEntryLimit !== null && c.relay > relayEntryLimit) ||
        (totalEntryLimit !== null && c.total > totalEntryLimit)
      ) {
        currentViolating.add(key)
      }
    })

    setEverViolatingKeys((prev) => {
      let changed = false
      const next = new Set(prev)
      currentViolating.forEach((key) => {
        if (!next.has(key)) {
          next.add(key)
          changed = true
        }
      })
      return changed ? next : prev
    })

    // Newly resolved = were violating last run, not anymore
    const newlyResolved = Array.from(prevViolatingRef.current).filter((k) => !currentViolating.has(k))
    prevViolatingRef.current = currentViolating

    if (newlyResolved.length === 0) return

    setExitingViolatingKeys((prev) => {
      const next = new Set(prev)
      newlyResolved.forEach((k) => next.add(k))
      return next
    })
    const timer = setTimeout(() => {
      setExitingViolatingKeys((prev) => {
        const next = new Set(prev)
        newlyResolved.forEach((k) => next.delete(k))
        return next
      })
    }, 1150)
    return () => clearTimeout(timer)
  }, [sd3Contents, deselectedEntryKeys, relayLegReplacements, individualEntryLimit, relayEntryLimit, totalEntryLimit])

  const hasLimits =
    individualEntryLimit !== null || relayEntryLimit !== null || totalEntryLimit !== null

  if (!hasLimits) {
    return (
      <div style={{ padding: "16px 0", color: "#389e0d" }}>
        <b>No entry limits are configured for this meet</b> — you're free to proceed!
      </div>
    )
  }

  const allCounts = computeAllSwimmerCounts(
    sd3Contents,
    deselectedEntryKeys,
    relayLegReplacements,
  )

  function isViolating(counts: ISwimmerEntryCounts): boolean {
    if (individualEntryLimit !== null && counts.individual > individualEntryLimit) return true
    if (relayEntryLimit !== null && counts.relay > relayEntryLimit) return true
    if (totalEntryLimit !== null && counts.total > totalEntryLimit) return true
    return false
  }

  function swimmerKey(clubName: string, sw: ISwimmerInSd3): string {
    return `${clubName}|${sw.swimmerFirstName.toLowerCase()}|${sw.swimmerLastName.toLowerCase()}|${sw.swimmerDOB}|${sw.swimmerGender}`
  }

  const hasAnyViolation = Array.from(allCounts.values()).some(isViolating)
  const clubs = sd3Contents.swimData.clubs

  const limitsBanner = (
    <div
      style={{
        marginBottom: 12,
        padding: "8px 12px",
        background: "#e6f4ff",
        border: "1px solid #91caff",
        borderRadius: 6,
        fontSize: 13,
      }}
    >
      <b>Entry limits:</b> {limitLabel(individualEntryLimit, relayEntryLimit, totalEntryLimit)}
    </div>
  )

  // Renders a single swimmer's card — used for both violating and resolved swimmers.
  function renderSwimmerCard(
    sw: ISwimmerInSd3,
    club: typeof clubs[number],
    clubIdx: number,
    cardKey: string | number,
    currentlyViolating: boolean,
    isExiting = false,
  ) {
    const key = swimmerKey(sw.club, sw)
    const counts = allCounts.get(key) ?? { individual: 0, relay: 0, total: 0 }
    const indOver = individualEntryLimit !== null && counts.individual > individualEntryLimit
    const relOver = relayEntryLimit !== null && counts.relay > relayEntryLimit
    const totOver = totalEntryLimit !== null && counts.total > totalEntryLimit

    const myIndEntries = club.individualEntries
      .map((entry, entryIdx) => ({ entry, entryIdx }))
      .filter(({ entry }) => swimmerMatchesEntry(sw, entry))

    const myRelayLegs: {
      relay: typeof club.relayEntries[number]
      relayIdx: number
      legIdx: number
    }[] = []
    club.relayEntries.forEach((relay, relayIdx) => {
      relay.relayLegs.forEach((leg, legIdx) => {
        if (swimmerMatchesEntry(sw, leg)) {
          myRelayLegs.push({ relay, relayIdx, legIdx })
        }
      })
    })

    return (
      <div
        key={cardKey}
        style={{
          position: "relative",
          background: currentlyViolating ? "#fff1f0" : "#f6ffed",
          border: `1px solid ${currentlyViolating ? "#ffa39e" : "#b7eb8f"}`,
          borderRadius: 6,
          padding: "8px 12px",
          ...(isExiting && { animation: "elResolveCard 0.5s ease forwards" }),
        }}
      >
        {/* Green check overlay — plays during phase 1 of the exit animation */}
        {isExiting && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 5,
              background: "rgba(82,196,26,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
              pointerEvents: "none",
            }}
          >
            <svg width="72" height="72" viewBox="0 0 72 72">
              <circle
                cx="36"
                cy="36"
                r="32"
                fill="rgba(82,196,26,0.15)"
                stroke="#52c41a"
                strokeWidth="2.5"
              />
              <path
                d="M20 37 L30 47 L52 25"
                fill="none"
                stroke="#52c41a"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="50"
                strokeDashoffset="0"
                style={{ animation: "elDrawCheck 0.35s ease 0.1s both" }}
              />
            </svg>
          </div>
        )}
        <div
          style={{
            fontWeight: 600,
            fontSize: 13,
            marginBottom: 6,
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 6,
          }}
        >
          <span>
            {sw.swimmerFirstName} {sw.swimmerLastName}
          </span>
          <span style={{ fontWeight: 400, fontSize: 12, color: "#555" }}>
            {sw.swimmerGender} · {sw.swimmerDOB}
          </span>
        </div>

        {/* Count badges */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {individualEntryLimit !== null && (
            <span
              style={{
                padding: "2px 8px",
                borderRadius: 4,
                fontSize: 12,
                background: indOver ? "#fff1f0" : "#f6ffed",
                border: `1px solid ${indOver ? "#ffa39e" : "#b7eb8f"}`,
                color: indOver ? "#cf1322" : "#237804",
                fontWeight: 600,
              }}
            >
              Individual: {counts.individual} / {individualEntryLimit}
            </span>
          )}
          {relayEntryLimit !== null && (
            <span
              style={{
                padding: "2px 8px",
                borderRadius: 4,
                fontSize: 12,
                background: relOver ? "#fff1f0" : "#f6ffed",
                border: `1px solid ${relOver ? "#ffa39e" : "#b7eb8f"}`,
                color: relOver ? "#cf1322" : "#237804",
                fontWeight: 600,
              }}
            >
              Relay: {counts.relay} / {relayEntryLimit}
            </span>
          )}
          {totalEntryLimit !== null && (
            <span
              style={{
                padding: "2px 8px",
                borderRadius: 4,
                fontSize: 12,
                background: totOver ? "#fff1f0" : "#f6ffed",
                border: `1px solid ${totOver ? "#ffa39e" : "#b7eb8f"}`,
                color: totOver ? "#cf1322" : "#237804",
                fontWeight: 600,
              }}
            >
              Total: {counts.total} / {totalEntryLimit}
            </span>
          )}
        </div>

        {/* Individual entries */}
        {myIndEntries.length > 0 && (
          <div style={{ marginBottom: 6 }}>
            <div style={{ fontSize: 11, color: "#555", fontWeight: 600, marginBottom: 4 }}>
              Individual Entries:
            </div>
            <div style={{ display: "grid", gap: 3 }}>
              {myIndEntries.map(({ entry, entryIdx }) => {
                const entryKey = makeIndKey(clubIdx, entryIdx)
                const isDeselected = !!deselectedEntryKeys[entryKey]
                const isExhibition = entry.isExhibition === "1"
                const eventLabel = `${entry.eventDistance} ${entry.eventStroke} (${sd3AgeGroupToDb(entry.eventAge)} ${entry.eventGender})`
                return (
                  <label
                    key={entryIdx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 12,
                      cursor: "pointer",
                      opacity: isExhibition ? 0.6 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!isDeselected}
                      disabled={isExhibition}
                      onChange={() => {
                        if (!isExhibition) onToggleIndividualEntry(entryKey)
                      }}
                    />
                    <span style={{ textDecoration: isDeselected ? "line-through" : undefined }}>
                      {eventLabel}
                    </span>
                    {isExhibition && (
                      <span
                        style={{
                          fontSize: 10,
                          background: "#e6f7ff",
                          border: "1px solid #91d5ff",
                          borderRadius: 3,
                          padding: "1px 4px",
                          color: "#0958d9",
                        }}
                      >
                        exhibition
                      </span>
                    )}
                  </label>
                )
              })}
            </div>
          </div>
        )}

        {/* Relay legs */}
        {myRelayLegs.length > 0 && (
          <div>
            <div style={{ fontSize: 11, color: "#555", fontWeight: 600, marginBottom: 4 }}>
              Relay Legs:
            </div>
            <div style={{ display: "grid", gap: 3 }}>
              {myRelayLegs.map(({ relay, relayIdx, legIdx }) => {
                const legKey = makeRelKey(clubIdx, relayIdx, legIdx)
                const isDeselected = !!deselectedEntryKeys[legKey]
                const replacement = relayLegReplacements[legKey]
                const isExhibition = relay.isExhibition === "1"
                const eventLabel = `${relay.eventDistance} ${relay.eventStroke} Relay (${sd3AgeGroupToDb(relay.eventAge)} ${relay.eventGender}) — Leg ${relay.relayLegs[legIdx]?.teamPosition ?? legIdx + 1}`
                return (
                  <div
                    key={`${relayIdx}:${legIdx}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 12,
                      opacity: isExhibition ? 0.6 : 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!isDeselected}
                      disabled={isExhibition}
                      style={{ cursor: isExhibition ? "not-allowed" : "pointer" }}
                      onChange={() => {
                        if (!isExhibition) {
                          onToggleRelayLeg(legKey)
                          if (!isDeselected) {
                            // User just deselected → auto-open replacement picker
                            onOpenReplacementModal(legKey)
                          }
                        }
                      }}
                    />
                    <span
                      style={{
                        textDecoration: isDeselected && !replacement ? "line-through" : undefined,
                        color: replacement ? "#595959" : undefined,
                      }}
                    >
                      {eventLabel}
                    </span>
                    {isExhibition && (
                      <span
                        style={{
                          fontSize: 10,
                          background: "#e6f7ff",
                          border: "1px solid #91d5ff",
                          borderRadius: 3,
                          padding: "1px 4px",
                          color: "#0958d9",
                        }}
                      >
                        exhibition
                      </span>
                    )}
                    {isDeselected && !replacement && (
                      <button
                        onClick={() => onOpenReplacementModal(legKey)}
                        style={{
                          fontSize: 11,
                          padding: "1px 8px",
                          borderRadius: 4,
                          border: "1px solid #d9d9d9",
                          background: "#fafafa",
                          cursor: "pointer",
                          color: "#1677ff",
                        }}
                      >
                        Assign replacement
                      </button>
                    )}
                    {isDeselected && replacement && (
                      <span style={{ color: "#389e0d", fontSize: 11 }}>
                        → {replacement.swimmerFirstName} {replacement.swimmerLastName}
                        <button
                          onClick={() => onOpenReplacementModal(legKey)}
                          style={{
                            marginLeft: 6,
                            fontSize: 11,
                            padding: "1px 6px",
                            borderRadius: 4,
                            border: "1px solid #d9d9d9",
                            background: "#fafafa",
                            cursor: "pointer",
                            color: "#1677ff",
                          }}
                        >
                          Change
                        </button>
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {limitsBanner}

      {hasAnyViolation ? (
        <div style={{ color: "#cf1322", fontSize: 13, marginBottom: 4 }}>
          The following swimmers have too many entries. Remove entries until all swimmers are
          within the limits, then click <b>Next</b>.
        </div>
      ) : (
        <div style={{ color: "#389e0d", fontWeight: 600 }}>
          ✓ All swimmers are within entry limits
          {everViolatingKeys.size > 0 && (
            <span style={{ fontSize: 12, fontWeight: 400, color: "#555", marginLeft: 8 }}>
              Expand <b>Resolved</b> below to undo changes.
            </span>
          )}
        </div>
      )}

      {clubs.map((club, clubIdx) => {
        const clubFileKey = club.teamFullName
        const clubSwimmers = sd3Contents.swimmers.filter((sw) => sw.club === clubFileKey)
        const clubDisplayName = clubMapping[clubFileKey]?.clubName ?? clubFileKey

        const violatingSwimmers = clubSwimmers.filter((sw) => {
          const key = swimmerKey(clubFileKey, sw)
          const counts = allCounts.get(key)
          return counts ? isViolating(counts) : false
        })

        // Include exiting swimmers so their fade-out animation can play
        const visibleViolatingSwimmers = clubSwimmers.filter((sw) => {
          const key = swimmerKey(clubFileKey, sw)
          const counts = allCounts.get(key)
          return (counts ? isViolating(counts) : false) || exitingViolatingKeys.has(key)
        })

        // Exclude exiting swimmers — they're still shown in the violating section while animating
        const resolvedSwimmers = clubSwimmers.filter((sw) => {
          const key = swimmerKey(clubFileKey, sw)
          const counts = allCounts.get(key)
          return (
            everViolatingKeys.has(key) &&
            !(counts ? isViolating(counts) : false) &&
            !exitingViolatingKeys.has(key)
          )
        })

        if (visibleViolatingSwimmers.length === 0 && resolvedSwimmers.length === 0) return null

        const isExpanded = expandedResolvedClubs.has(clubIdx)

        return (
          <div
            key={clubIdx}
            style={{
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 8,
              padding: "10px 14px",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
              {clubDisplayName}
            </div>

            {/* Violating swimmers (includes cards animating out) */}
            {visibleViolatingSwimmers.length > 0 && (
              <div style={{ display: "grid", gap: 10 }}>
                {visibleViolatingSwimmers.map((sw) => {
                  const swKey = swimmerKey(clubFileKey, sw)
                  const isExiting = exitingViolatingKeys.has(swKey)
                  return (
                    <div
                      key={swKey}
                      style={{
                        overflow: "hidden",
                        ...(isExiting && { animation: "elExitRight 1.1s ease forwards" }),
                      }}
                    >
                      {renderSwimmerCard(sw, club, clubIdx, swKey, true, isExiting)}
                    </div>
                  )
                })}
              </div>
            )}

            {/* Resolved section — collapsed by default */}
            {resolvedSwimmers.length > 0 && (
              <div style={{ marginTop: visibleViolatingSwimmers.length > 0 ? 10 : 0 }}>
                <button
                  onClick={() =>
                    setExpandedResolvedClubs((prev) => {
                      const next = new Set(prev)
                      if (next.has(clubIdx)) next.delete(clubIdx)
                      else next.add(clubIdx)
                      return next
                    })
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 12,
                    color: "#555",
                    padding: "4px 0",
                    fontWeight: 600,
                  }}
                >
                  <span style={{ fontSize: 10 }}>{isExpanded ? "▾" : "▸"}</span>
                  Resolved ({resolvedSwimmers.length})
                </button>
                {isExpanded && (
                  <div style={{ display: "grid", gap: 10, marginTop: 6 }}>
                    {resolvedSwimmers.map((sw, swIdx) =>
                      renderSwimmerCard(sw, club, clubIdx, `r${swIdx}`, false),
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

interface IRelayReplacementModalProps {
  legKey: string | null
  sd3Contents: ISd3Data
  clubMapping: { [key: string]: { clubId: number; clubName: string } }
  deselectedEntryKeys: Record<EntryKey, boolean>
  relayLegReplacements: Record<EntryKey, IRelayLegReplacement>
  individualEntryLimit: number | null
  relayEntryLimit: number | null
  totalEntryLimit: number | null
  onClose: () => void
  onConfirm: (legKey: string, replacement: IRelayLegReplacement | null) => void
}

function RelayReplacementModal({
  legKey,
  sd3Contents,
  clubMapping,
  deselectedEntryKeys,
  relayLegReplacements,
  individualEntryLimit,
  relayEntryLimit,
  totalEntryLimit,
  onClose,
  onConfirm,
}: IRelayReplacementModalProps): JSX.Element | null {
  const [selectedSwimmerKey, setSelectedSwimmerKey] = useState<string | null>(null)

  // Reset selection when modal opens for a new leg
  useEffect(() => {
    setSelectedSwimmerKey(null)
  }, [legKey])

  if (!legKey) return null

  // Parse the key to find the relay entry
  const parts = legKey.split(":")
  const clubIdx = parseInt(parts[1], 10)
  const relayIdx = parseInt(parts[2], 10)
  const legIdx = parseInt(parts[3], 10)

  const club = sd3Contents.swimData.clubs[clubIdx]
  if (!club) return null
  const relay = club.relayEntries[relayIdx]
  if (!relay) return null
  const originalLeg = relay.relayLegs[legIdx]
  if (!originalLeg) return null

  const clubFileKey = club.teamFullName
  const eventLabel = `${relay.eventDistance} ${relay.eventStroke} Relay (${sd3AgeGroupToDb(relay.eventAge)} ${relay.eventGender})`

  // Candidate pool: all swimmers in this club from the import file, excluding the original leg swimmer
  // and excluding swimmers already in other legs of this relay
  const otherLegsInRelay = relay.relayLegs
    .map((leg, i) => ({ leg, i }))
    .filter(({ i }) => i !== legIdx)
    .map(({ leg, i }) => {
      // If another leg is deselected with a replacement, use replacement
      const otherKey = makeRelKey(clubIdx, relayIdx, i)
      if (deselectedEntryKeys[otherKey] && relayLegReplacements[otherKey]) {
        return relayLegReplacements[otherKey]
      }
      if (deselectedEntryKeys[otherKey]) return null // leg removed, slot available
      return leg
    })
    .filter(Boolean)

  // Current counts (for eligibility checking)
  const allCounts = computeAllSwimmerCounts(sd3Contents, deselectedEntryKeys, relayLegReplacements)

  function getSwKey(sw: ISwimmerInSd3): string {
    return `${clubFileKey}|${sw.swimmerFirstName.toLowerCase()}|${sw.swimmerLastName.toLowerCase()}|${sw.swimmerDOB}|${sw.swimmerGender}`
  }

  // Check if a candidate would exceed limits by taking this relay leg
  function wouldViolate(sw: ISwimmerInSd3): boolean {
    const existing = allCounts.get(getSwKey(sw)) ?? { individual: 0, relay: 0, total: 0 }
    const projectedRelay = existing.relay + 1
    const projectedTotal = existing.total + 1
    if (relayEntryLimit !== null && projectedRelay > relayEntryLimit) return true
    if (totalEntryLimit !== null && projectedTotal > totalEntryLimit) return true
    return false
  }

  const candidates = sd3Contents.swimmers
    .filter((sw) => sw.club === clubFileKey)
    .filter((sw) => !swimmerMatchesEntry(sw, originalLeg))
    .filter((sw) => !otherLegsInRelay.some((other) => other && swimmerMatchesEntry(sw, other)))

  const eligibleCandidates = candidates.filter((sw) => !wouldViolate(sw))
  const ineligibleCandidates = candidates.filter((sw) => wouldViolate(sw))

  const currentReplacement = relayLegReplacements[legKey]
  const displaySelected = selectedSwimmerKey ?? (currentReplacement
    ? `${currentReplacement.swimmerFirstName.toLowerCase()}|${currentReplacement.swimmerLastName.toLowerCase()}|${currentReplacement.swimmerDOB}|${currentReplacement.swimmerGender}`
    : null)

  function renderCandidate(sw: ISwimmerInSd3, ineligible: boolean) {
    const candidateKey = `${sw.swimmerFirstName.toLowerCase()}|${sw.swimmerLastName.toLowerCase()}|${sw.swimmerDOB}|${sw.swimmerGender}`
    const isSelected = displaySelected === candidateKey
    const counts = allCounts.get(getSwKey(sw)) ?? { individual: 0, relay: 0, total: 0 }
    return (
      <button
        key={candidateKey}
        disabled={ineligible}
        onClick={() => setSelectedSwimmerKey(isSelected ? null : candidateKey)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "6px 10px",
          border: isSelected ? "2px solid #52c41a" : "1px solid #d9d9d9",
          borderRadius: 6,
          background: isSelected ? "#f6ffed" : ineligible ? "#fafafa" : "#fff",
          cursor: ineligible ? "not-allowed" : "pointer",
          textAlign: "left",
          width: "100%",
          opacity: ineligible ? 0.5 : 1,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600 }}>
          {sw.swimmerFirstName} {sw.swimmerLastName}
          <span style={{ fontSize: 11, fontWeight: 400, color: "#555", marginLeft: 6 }}>
            {sw.swimmerGender} · {sw.swimmerDOB}
          </span>
        </span>
        <span style={{ fontSize: 11, color: "#555", whiteSpace: "nowrap", marginLeft: 8 }}>
          {relayEntryLimit !== null && `${counts.relay} relay`}
          {relayEntryLimit !== null && totalEntryLimit !== null && " · "}
          {totalEntryLimit !== null && `${counts.total} total`}
        </span>
      </button>
    )
  }

  function handleOk() {
    if (!legKey) return
    if (!displaySelected) {
      // "Leave empty" — no replacement
      onConfirm(legKey, null)
    } else {
      const sw = candidates.find((sw) => {
        const k = `${sw.swimmerFirstName.toLowerCase()}|${sw.swimmerLastName.toLowerCase()}|${sw.swimmerDOB}|${sw.swimmerGender}`
        return k === displaySelected
      })
      if (!sw) {
        onConfirm(legKey, null)
        return
      }
      onConfirm(legKey, {
        swimmerId: sw.swimmerId,
        swimmerFirstName: sw.swimmerFirstName,
        swimmerLastName: sw.swimmerLastName,
        swimmerDOB: sw.swimmerDOB,
        swimmerGender: sw.swimmerGender,
      })
    }
  }

  return (
    <Modal
      title={`Replace relay leg: ${eventLabel}`}
      open={!!legKey}
      onCancel={onClose}
      onOk={handleOk}
      okText="Confirm"
      width={480}
    >
      <p style={{ fontSize: 13, color: "#555", marginBottom: 12 }}>
        <b>
          {originalLeg.swimmerFirstName} {originalLeg.swimmerLastName}
        </b>{" "}
        is being removed from this leg. Optionally select a replacement swimmer, or leave the
        position empty.
      </p>

      {eligibleCandidates.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 6 }}>
            Eligible replacements:
          </div>
          <div style={{ display: "grid", gap: 4 }}>
            {eligibleCandidates.map((sw) => renderCandidate(sw, false))}
          </div>
        </div>
      )}

      {ineligibleCandidates.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#aaa", marginBottom: 6 }}>
            Already at entry limit (ineligible):
          </div>
          <div style={{ display: "grid", gap: 4 }}>
            {ineligibleCandidates.map((sw) => renderCandidate(sw, true))}
          </div>
        </div>
      )}

      {eligibleCandidates.length === 0 && ineligibleCandidates.length === 0 && (
        <p style={{ color: "#aaa", fontSize: 13 }}>
          No other swimmers from this club are in the import file.
        </p>
      )}

      <div style={{ marginTop: 8, fontSize: 12, color: "#888" }}>
        If no replacement is selected, the position will be left empty.
      </div>
    </Modal>
  )
}

export default function ImportEntriesModal({
  mode = "entries",
  visible,
  setVisible,
  meetId,
  refresh,
  individualEntryLimit = null,
  relayEntryLimit = null,
  totalEntryLimit = null,
}: IImportEntriesModalProps): JSX.Element {
  const [loading, setLoading] = useState(false)
  const [sd3Contents, setSd3Contents] = useState<ISd3Data | null>(null)
  const [sd3Validation, setSd3Validation] = useState<ISd3Validation>({
    dateOfBirthValid: true,
    invalidBirthdaySwimmers: [],
  })
  const [clubMapping, setClubMapping] = useState<{
    [key: string]: { clubId: number; clubName: string }
  }>({})
  const [sd3LoadStep, setSd3LoadStep] = useState<Sd3LoadStep>("file-drop")
  const [summaryData, setSummaryData] = useState<IImportSummary | null>(null)
  const [pendingZipFiles, setPendingZipFiles] = useState<{
    zipFile: JSZip
    validFiles: string[]
  } | null>(null)
  const [selectedZipFileName, setSelectedZipFileName] = useState<string | null>(null)
  const [meetClubs, setMeetClubs] = useState<IClub[]>([])
  const [okButtonDisabled, setOkButtonDisabled] = useState(true)
  const [meetEventOrder, setMeetEventOrder] = useState<MeetEvent[]>([])
  const [eventOrderErrors, setEventOrderErrors] = useState<string[]>([])
  const [hasNaNEventNumbers, setHasNaNEventNumbers] = useState(false)
  const [nanWarningAcknowledged, setNanWarningAcknowledged] = useState(false)
  const [deselectedEntryKeys, setDeselectedEntryKeys] = useState<Record<EntryKey, boolean>>({})
  const [relayLegReplacements, setRelayLegReplacements] = useState<Record<EntryKey, IRelayLegReplacement>>({})
  const [replacementModalLegKey, setReplacementModalLegKey] = useState<string | null>(null)
  // Results mode — HY3 specific
  const [rawHy3FileContents, setRawHy3FileContents] = useState<string | null>(null)
  const [hy3ImportScope, setHy3ImportScope] = useState<"finals" | "prelims" | "both">("finals")
  const [hy3MatchOnEventNumbers, setHy3MatchOnEventNumbers] = useState(true)
  const [hy3HasPrelimsFinals, setHy3HasPrelimsFinals] = useState(false)
  const [dobFixes, setDobFixes] = useState<Record<string, string>>({})
  const [effectiveMeetId, setEffectiveMeetId] = useState<number>(meetId)
  const [sessionRoundMapping, setSessionRoundMapping] = useState<{
    prelims: number | "skip"
    finals: number | "skip"
  } | null>(null)
  const [siblingSessions, setSiblingSessions] = useState<Array<{
    meetId: number
    sessionType: string
    sessionOrder: number
    sessionDate: string
    meetName: string
    seriesName: string
  }>>([])
  const [sessionContext, setSessionContext] = useState<{
    sessionType: "timedFinals" | "prelims" | "finals"
    sessionOrder: number
    totalSessions: number
    multiSessionMeetId: number
    seriesName: string
  } | null>(null)

  useEffect(() => {
    if (!visible || !meetId || meetId <= 0) {
      setSessionContext(null)
      return
    }
    swimminglyApi.get(gen(`/api/getMeetSessionContext/${meetId}`)).then((data: any) => {
      if (data.status === "success") {
        setSessionContext(data.sessionContext ?? null)
      }
    })
  }, [visible, meetId])

  // Auto-suggest hy3ImportScope based on session type
  useEffect(() => {
    if (!sessionContext) return
    if (sessionContext.sessionType === "prelims") setHy3ImportScope("prelims")
    else if (sessionContext.sessionType === "timedFinals") setHy3ImportScope("finals")
    // finals sessions: entry import is blocked; results import stays as "finals"
  }, [sessionContext])

  // Fetch sibling sessions so the user can pick which session to import into
  useEffect(() => {
    if (!sessionContext || sessionContext.totalSessions <= 1) {
      setSiblingSessions([])
      return
    }
    swimminglyApi.get(gen(`/api/getMultiSessionSiblings/${meetId}`)).then((data: any) => {
      if (data.status === "success") {
        setSiblingSessions(data.sessions ?? [])
      }
    })
  }, [sessionContext, meetId])

  useEffect(() => {
    let mounted = true
    if (effectiveMeetId && effectiveMeetId > 0) {
      swimminglyApi.get(gen(`/api/getMeetEvents/${effectiveMeetId}`)).then((data) => {
        if (data.status === "success" && mounted) {
          setMeetEventOrder(data.events)
        }
      })
    }
    return () => {
      mounted = false
    }
  }, [effectiveMeetId, visible])

  useEffect(() => {
    const newEventOrderErrors: string[] = []
    const sd3Events = sd3Contents?.meetEvents
    if (sd3Events) {
      const fileHasNaNEvents = sd3Events.some((e) => isNaN(e.eventNumber))
      for (let i = 0; i < sd3Events.length; i++) {
        const sd3AgeGroupDb = sd3AgeGroupToDb(sd3Events[i].ageGroup)
        let foundMatch = false
        for (let j = 0; j < meetEventOrder.length; j++) {
          const numberMatches =
            !hy3MatchOnEventNumbers ||
            fileHasNaNEvents ||
            isNaN(sd3Events[i].eventNumber) ||
            sd3Events[i].eventNumber === meetEventOrder[j].eventNumber
          const strokeMatches =
            sd3Events[i].stroke.toLowerCase().trim() ===
            meetEventOrder[j].stroke.toLowerCase().trim()
          const distanceMatches =
            String(sd3Events[i].distance) === String(meetEventOrder[j].distance)
          const genderMatches = sd3Events[i].gender === meetEventOrder[j].gender
          const ageGroupMatches =
            sd3AgeGroupDb.toLowerCase() ===
            (meetEventOrder[j].ageGroup || "").toLowerCase()
          if (
            numberMatches &&
            strokeMatches &&
            distanceMatches &&
            genderMatches &&
            ageGroupMatches
          ) {
            foundMatch = true
            break
          }
        }
        if (!foundMatch) {
          const sd3EventDescr = `${sd3Events[i].gender} ${sd3Events[i].distance} ${sd3Events[i].stroke} (${sd3AgeGroupDb})`
          newEventOrderErrors.push(
            `No match found in this meet for: ${sd3EventDescr}`,
          )
        }
      }
    }
    setEventOrderErrors(newEventOrderErrors)
  }, [meetEventOrder, sd3Contents, hy3MatchOnEventNumbers])

  useEffect(() => {
    const fileHasNaN =
      sd3Contents?.meetEvents.some((e) => isNaN(e.eventNumber)) ?? false
    setHasNaNEventNumbers(fileHasNaN)
  }, [sd3Contents?.meetEvents])

  useEffect(() => {
    if (
      sd3LoadStep === "club-map" &&
      hasNaNEventNumbers &&
      !nanWarningAcknowledged &&
      hy3MatchOnEventNumbers
    ) {
      setSd3LoadStep("no-event-numbers-warning")
    }
  }, [sd3LoadStep, hasNaNEventNumbers, nanWarningAcknowledged, hy3MatchOnEventNumbers])

  useEffect(() => {
    setLoading(false)
    setSd3LoadStep("file-drop")
    setSd3Contents(null)
    setSummaryData(null)
    setPendingZipFiles(null)
    setSelectedZipFileName(null)
    setMeetClubs([])
    setClubMapping({})
    setOkButtonDisabled(true)
    setHasNaNEventNumbers(false)
    setNanWarningAcknowledged(false)
    setDeselectedEntryKeys({})
    setRelayLegReplacements({})
    setReplacementModalLegKey(null)
    setRawHy3FileContents(null)
    setHy3ImportScope("finals")
    setHy3MatchOnEventNumbers(true)
    setDobFixes({})
    setSessionContext(null)
    setSiblingSessions([])
    setSessionRoundMapping(null)
    setEffectiveMeetId(meetId)
    if (!visible) {
      setMeetEventOrder([])
    }
    setEventOrderErrors([])
  }, [visible])

  useEffect(() => {
    if (visible && effectiveMeetId && effectiveMeetId !== -1) {
      swimminglyApi.get(gen(`/api/getMeetClubs/${effectiveMeetId}`)).then((data) => {
        if (data.status === "success") {
          setMeetClubs(data.meetClubs)
        }
      })
    } else {
      setMeetClubs([])
    }
  }, [effectiveMeetId, visible])

  useEffect(() => {
    let newOkButtonDisabled = true
    if (sd3LoadStep === "no-event-numbers-warning" || sd3LoadStep === "hy3-options" || sd3LoadStep === "file-options") {
      if (okButtonDisabled !== false) setOkButtonDisabled(false)
      return
    } else if (sd3LoadStep === "club-map") {
      if (Object.keys(clubMapping).length === sd3Contents?.clubNames.length) {
        newOkButtonDisabled = false
      }
      if (newOkButtonDisabled !== okButtonDisabled)
        setOkButtonDisabled(newOkButtonDisabled)
    } else if (
      sd3LoadStep === "choose-swimmers" &&
      okButtonDisabled !== false
    ) {
      setOkButtonDisabled(false)
    } else if (sd3LoadStep === "zip-file-picker") {
      setOkButtonDisabled(!selectedZipFileName)
    } else if (sd3LoadStep === "entry-limits") {
      // Disabled if any swimmer has a violation
      if (sd3Contents) {
        const allCounts = computeAllSwimmerCounts(sd3Contents, deselectedEntryKeys, relayLegReplacements)
        const hasViolation = Array.from(allCounts.values()).some((c) => {
          if (individualEntryLimit !== null && c.individual > individualEntryLimit) return true
          if (relayEntryLimit !== null && c.relay > relayEntryLimit) return true
          if (totalEntryLimit !== null && c.total > totalEntryLimit) return true
          return false
        })
        setOkButtonDisabled(hasViolation)
      } else {
        setOkButtonDisabled(false)
      }
    } else if (sd3LoadStep === "load-data" || sd3LoadStep === "summary") {
      setOkButtonDisabled(false)
    } else if (sd3LoadStep === "invalid-sd3") {
      setOkButtonDisabled(false)
    } else if (sd3LoadStep === "session-map") {
      const bothSkipped = sessionRoundMapping?.prelims === "skip" && sessionRoundMapping?.finals === "skip"
      setOkButtonDisabled(!!bothSkipped)
    } else if (sd3LoadStep === "fix-missing-dob") {
      const swimmersNeedingDob = sd3Contents?.swimmers.filter(
        (sw) =>
          (!sw.swimmerId || sw.swimmerId <= 0) &&
          (!sw.swimmerDOB || sw.swimmerDOB === "" || sw.swimmerDOB.startsWith("age-")),
      ) ?? []
      const allFilled =
        swimmersNeedingDob.length === 0 ||
        swimmersNeedingDob.every((sw) => {
          const key = `${sw.club}|${sw.swimmerFirstName}|${sw.swimmerLastName}|${sw.swimmerGender}`
          const val = dobFixes[key]
          return val && /^\d{4}-\d{2}-\d{2}$/.test(val)
        })
      setOkButtonDisabled(!allFilled)
    }
  }, [
    okButtonDisabled,
    sd3LoadStep,
    clubMapping,
    sd3Contents,
    selectedZipFileName,
    deselectedEntryKeys,
    relayLegReplacements,
    individualEntryLimit,
    relayEntryLimit,
    totalEntryLimit,
    dobFixes,
    sessionRoundMapping,
  ])

  const getSwimmersWhoWillNotBeUploaded = useCallback((): ISwimmerInSd3[] => {
    if (!sd3Contents?.swimmers) return []
    const allSd3Swimmers = sd3Contents?.swimmers
    const unmappedSd3Swimmers = allSd3Swimmers.filter(
      (sd3Swimmer) => sd3Swimmer.swimmerId === -1,
    )
    const unmappedSd3SwimmersOnPayingClubs = unmappedSd3Swimmers.filter(
      (unmappedSwimmer) =>
        meetClubs.find(
          (mC) => mC.clubId === clubMapping[unmappedSwimmer.club].clubId,
        )?.isSwimminglyCustomer === 1,
    )
    unmappedSd3SwimmersOnPayingClubs.sort((sw1, sw2) => {
      if (clubMapping[sw1.club].clubName > clubMapping[sw2.club].clubName)
        return 1
      if (clubMapping[sw1.club].clubName < clubMapping[sw2.club].clubName)
        return -1
      if (sw1.swimmerLastName > sw2.swimmerLastName) return 1
      if (sw1.swimmerLastName < sw2.swimmerLastName) return -1
      if (sw1.swimmerFirstName > sw2.swimmerFirstName) return 1
      if (sw1.swimmerFirstName < sw2.swimmerFirstName) return -1
      return 1
    })
    return unmappedSd3SwimmersOnPayingClubs
  }, [sd3Contents?.swimmers, meetClubs, clubMapping])

  return (
    <Modal
      title={mode === "results" ? "Import Results" : "Import Entries"}
      open={visible}
      onCancel={() => setVisible(false)}
      onOk={() => {
        if (sd3LoadStep === "summary") {
          setVisible(false)
          refresh()
          return
        }
        if (sd3LoadStep === "hy3-options") {
          if (!rawHy3FileContents) return
          // Detect P&F: scan for both E2F and E2P (or F2F and F2P) records
          const detectPrelimsFinals = (contents: string): boolean => {
            let hasF = false, hasP = false
            for (const line of contents.split("\n")) {
              if (line.length >= 3 && (line.startsWith("E2") || line.startsWith("F2"))) {
                if (line[2] === "F") hasF = true
                if (line[2] === "P") hasP = true
                if (hasF && hasP) return true
              }
            }
            return false
          }
          const isPrelimsFinals = detectPrelimsFinals(rawHy3FileContents)
          setHy3HasPrelimsFinals(isPrelimsFinals)
          // Always parse with "both" so entries have round fields set
          const parseScope = isPrelimsFinals ? "both" : hy3ImportScope
          const hy3Options: IHy3ParseOptions = {
            importScope: parseScope,
            matchOnEventNumbers: hy3MatchOnEventNumbers,
          }
          const swimData = parseHy3Results(rawHy3FileContents, hy3Options)
          const clubNames = swimData.clubs.map((c) => c.teamFullName)
          const swimmers = findUniqueHy3Swimmers(swimData)
          setSd3Contents({
            clubNames,
            swimmers,
            swimData,
            meetEvents: [], // HY3 skips event-order validation
          })
          if (isPrelimsFinals && hy3ImportScope === "both") {
            if (siblingSessions.length > 1) {
              // Smart defaults: match prelims/finals round to the corresponding session type
              const prelimsSibling = siblingSessions.find((s) => s.sessionType === "prelims")
              const finalsSibling = siblingSessions.find((s) => s.sessionType === "finals") ??
                siblingSessions[siblingSessions.length - 1]
              setSessionRoundMapping({
                prelims: prelimsSibling?.meetId ?? siblingSessions[0].meetId,
                finals: finalsSibling?.meetId ?? siblingSessions[siblingSessions.length - 1].meetId,
              })
              setSd3LoadStep("session-map")
            } else {
              setSd3LoadStep("prelims-finals-detected")
            }
          } else {
            setSd3LoadStep("club-map")
          }
          return
        }
        if (sd3LoadStep === "prelims-finals-detected") {
          setSd3LoadStep("club-map")
          return
        }
        if (sd3LoadStep === "session-map") {
          // Set effectiveMeetId to the primary (finals or first non-skip) session for club-map/choose-swimmers
          const primaryId = sessionRoundMapping?.finals !== "skip"
            ? sessionRoundMapping?.finals
            : sessionRoundMapping?.prelims !== "skip"
            ? sessionRoundMapping?.prelims
            : null
          if (typeof primaryId === "number") setEffectiveMeetId(primaryId)
          setSd3LoadStep("club-map")
          return
        }
        if (sd3LoadStep === "zip-file-picker") {
          if (!selectedZipFileName || !pendingZipFiles) return
          setLoading(true)
          void pendingZipFiles.zipFile
            .file(selectedZipFileName)
            ?.async("string")
            .then((contents) => {
              if (!contents) {
                message.error(
                  "Could not read the selected file from the zip archive.",
                )
                return
              }
              const ext =
                selectedZipFileName.split(".").pop()?.toLowerCase() ?? ""
              if (ext === "hy3") {
                const parsedHy3 = parseHy3Entries(contents)
                setSd3Contents(parsedHy3)
                setSd3Validation({
                  dateOfBirthValid: true,
                  invalidBirthdaySwimmers: [],
                })
                setSd3LoadStep("file-options")
              } else {
                const sd3ValidationResult = validateSd3(contents)
                setSd3Validation(sd3ValidationResult)
                const parsedSd3 = parseSd3(contents)
                setSd3Contents(parsedSd3)
                setSd3LoadStep("file-options")
              }
              setPendingZipFiles(null)
              setSelectedZipFileName(null)
            })
            .catch(() => {
              message.error(
                "Failed to read the selected file from the zip archive.",
              )
            })
            .finally(() => {
              setLoading(false)
            })
          return
        }
        if (sd3LoadStep === "no-event-numbers-warning") {
          setNanWarningAcknowledged(true)
          setSd3LoadStep("club-map")
          return
        }
        if (sd3LoadStep === "club-map") {
          const newClubMapping = cloneDeep(clubMapping)
          const newSd3Contents = cloneDeep(sd3Contents)
          for (let theKey of Object.keys(newClubMapping)) {
            if (newClubMapping[theKey].clubId === -2) {
              if (newSd3Contents?.clubNames) {
                newSd3Contents.clubNames = newSd3Contents.clubNames.filter(
                  (cN) => cN !== theKey,
                )
              }
              if (newSd3Contents?.swimmers) {
                newSd3Contents.swimmers = newSd3Contents.swimmers.filter(
                  (sw) => sw.club !== theKey,
                )
              }
              if (newSd3Contents?.swimData.clubs) {
                newSd3Contents.swimData.clubs =
                  newSd3Contents.swimData.clubs.filter(
                    (cl) => cl.teamFullName !== theKey,
                  )
              }
              delete newClubMapping[theKey]
            }
          }
          setClubMapping(newClubMapping)
          setSd3Contents(newSd3Contents)
          setSd3LoadStep("choose-swimmers")
        } else if (sd3LoadStep === "choose-swimmers") {
          Sd3Manager.emit("saveSwimmerMapping")
          // Routing is handled inside ChooseSwimmers' saveSwimmerMapping listener
        } else if (sd3LoadStep === "entry-limits") {
          if (sd3Contents) {
            const filtered = applyEntryLimitChoices(sd3Contents, deselectedEntryKeys, relayLegReplacements)
            setSd3Contents(filtered)
          }
          setSd3LoadStep("load-data")
        } else if (sd3LoadStep === "load-data") {
          if (Object.keys(clubMapping).length === 0) {
            message.error("Please map at least one club before importing.")
            return
          }
          if (loading) return
          setLoading(true)

          // ── Prelims + Finals mapped to existing multi-session meet sessions ──
          if (mode === "results" && hy3HasPrelimsFinals && hy3ImportScope === "both" && sd3Contents && sessionRoundMapping) {
            const filterByRound = (round: "prelims" | "finals") => ({
              ...sd3Contents,
              swimData: {
                ...sd3Contents.swimData,
                clubs: sd3Contents.swimData.clubs.map((club) => ({
                  ...club,
                  individualEntries: club.individualEntries.filter(
                    (e: any) => e.round === round,
                  ),
                  relayEntries: club.relayEntries.filter(
                    (e: any) => e.round === round || e.round == null,
                  ),
                })),
              },
            })
            const matchEvNums = { matchOnEventNumbers: hy3MatchOnEventNumbers }
            const rounds: Array<"prelims" | "finals"> = ["prelims", "finals"]
            const importSteps = rounds
              .filter((r) => sessionRoundMapping[r] !== "skip")
              .map((r) => () =>
                swimminglyApi
                  .post(gen(`/api/createMeetResults/${sessionRoundMapping[r]!}`))
                  .body({ sd3Contents: filterByRound(r), clubMapping, ...matchEvNums })
                  .then((d: any) => {
                    if (d.status !== "success") throw new Error(`${r} import failed`)
                  }),
              )
            importSteps
              .reduce((p, fn) => p.then(fn), Promise.resolve())
              .then(() => message.success("Import complete!"))
              .catch(() => message.error("Uh oh, something went wrong loading your meet results."))
              .finally(() => { setLoading(false); setVisible(false); refresh() })
            return
          }

          // ── Prelims + Finals two-meetId flow (creates new session) ────────
          if (mode === "results" && hy3HasPrelimsFinals && hy3ImportScope === "both" && sd3Contents) {
            const filterByRound = (round: "prelims" | "finals") => ({
              ...sd3Contents,
              swimData: {
                ...sd3Contents.swimData,
                clubs: sd3Contents.swimData.clubs.map((club) => ({
                  ...club,
                  individualEntries: club.individualEntries.filter(
                    (e: any) => e.round === round,
                  ),
                  relayEntries: club.relayEntries.filter(
                    (e: any) => e.round === round || e.round == null,
                  ),
                })),
              },
            })

            swimminglyApi
              .post(gen(`/api/createPrelimsSessionForMeet/${effectiveMeetId}`))
              .body({})
              .then((sessionData: any) => {
                if (sessionData.status !== "success") {
                  message.error("Failed to create prelims session: " + (sessionData.error ?? "Unknown error"))
                  setLoading(false)
                  return
                }
                const { prelimsMeetId } = sessionData
                const matchEvNums = { matchOnEventNumbers: hy3MatchOnEventNumbers }
                // POST prelims then finals in sequence
                return swimminglyApi
                  .post(gen(`/api/createMeetResults/${prelimsMeetId}`))
                  .body({ sd3Contents: filterByRound("prelims"), clubMapping, ...matchEvNums })
                  .then((prelData: any) => {
                    if (prelData.status !== "success") throw new Error("Prelims import failed")
                    return swimminglyApi
                      .post(gen(`/api/createMeetResults/${effectiveMeetId}`))
                      .body({ sd3Contents: filterByRound("finals"), clubMapping, ...matchEvNums })
                  })
                  .then((finData: any) => {
                    if (finData.status !== "success") throw new Error("Finals import failed")
                    message.success("Prelims + Finals imported successfully!")
                  })
              })
              .catch((_err: any) => {
                message.error("Uh oh, something went wrong loading your meet results.")
              })
              .finally(() => {
                setLoading(false)
                setVisible(false)
                refresh()
              })
            return
          }

          // ── Standard single-meetId flow ───────────────────────────────────
          const apiPath =
            mode === "results"
              ? `/api/createMeetResults/${effectiveMeetId}`
              : `/api/createMeetEntries/${effectiveMeetId}`
          const apiBody = { sd3Contents, clubMapping, matchOnEventNumbers: hy3MatchOnEventNumbers }
          swimminglyApi
            .post(gen(apiPath))
            .body(apiBody)
            .then((data) => {
              if (data.status === "success") {
                if (mode === "results") {
                  message.success("Success!")
                } else {
                  setSummaryData(data.summary ?? null)
                  setSd3LoadStep("summary")
                }
              } else {
                message.error(
                  mode === "results"
                    ? "Uh oh, we had an issue loading your meet results."
                    : "Uh oh, we had an issue loading your meet entries.",
                )
              }
            })
            .catch((_err) => {
              message.error(
                mode === "results"
                  ? "Uh oh, something went wrong loading your meet results."
                  : "Uh oh, something went wrong loading your meet entries.",
              )
            })
            .finally(() => {
              setLoading(false)
              if (mode === "results") {
                setVisible(false)
                refresh()
              }
            })
        } else if (sd3LoadStep === "file-options") {
          // Always proceed — fix-missing-dob handles unmatched swimmers with missing DOBs after choose-swimmers
          setSd3LoadStep("club-map")
        } else if (sd3LoadStep === "invalid-sd3") {
          setSd3LoadStep("club-map")
        } else if (sd3LoadStep === "fix-missing-dob") {
          const newSd3Contents = cloneDeep(sd3Contents)
          if (newSd3Contents) {
            for (const sw of newSd3Contents.swimmers) {
              if (!sw.swimmerDOB || sw.swimmerDOB === "" || sw.swimmerDOB.startsWith("age-")) {
                const key = `${sw.club}|${sw.swimmerFirstName}|${sw.swimmerLastName}|${sw.swimmerGender}`
                if (dobFixes[key]) sw.swimmerDOB = dobFixes[key]
              }
            }
            for (const clubData of newSd3Contents.swimData.clubs) {
              const clubKey = clubData.teamFullName
              for (const entry of clubData.individualEntries) {
                if (!entry.swimmerDOB || entry.swimmerDOB === "" || entry.swimmerDOB.startsWith("age-")) {
                  const key = `${clubKey}|${entry.swimmerFirstName}|${entry.swimmerLastName}|${entry.swimmerGender}`
                  if (dobFixes[key]) entry.swimmerDOB = dobFixes[key]
                }
              }
              for (const relay of clubData.relayEntries) {
                for (const leg of relay.relayLegs) {
                  if (!leg.swimmerDOB || leg.swimmerDOB === "" || leg.swimmerDOB.startsWith("age-")) {
                    const key = `${clubKey}|${leg.swimmerFirstName}|${leg.swimmerLastName}|${leg.swimmerGender}`
                    if (dobFixes[key]) leg.swimmerDOB = dobFixes[key]
                  }
                }
              }
            }
            setSd3Contents(newSd3Contents)
          }
          setSd3LoadStep(mode === "results" ? "load-data" : "entry-limits")
        }
      }}
      okButtonProps={{
        disabled:
          okButtonDisabled ||
          (sd3LoadStep === "file-drop" &&
            mode === "entries" &&
            sessionContext?.sessionType === "finals"),
        loading,
      }}
      okText={
        sd3LoadStep === "summary"
          ? "Done"
          : sd3LoadStep === "zip-file-picker"
          ? "Next"
          : sd3LoadStep === "file-options"
          ? "Next"
          : sd3LoadStep === "invalid-sd3"
          ? "Proceed anyway"
          : sd3LoadStep === "fix-missing-dob"
          ? "Next"
          : sd3LoadStep === "no-event-numbers-warning"
          ? "Proceed"
          : sd3LoadStep === "entry-limits"
          ? "Next"
          : sd3LoadStep === "session-map"
          ? "Next"
          : sd3LoadStep === "load-data" ||
            (!hasNaNEventNumbers && eventOrderErrors.length > 0)
          ? "Ok"
          : "Next"
      }
    >
      {sessionContext && (
        <div
          style={{
            marginBottom: 12,
            padding: "6px 12px",
            background: "#f0f5ff",
            border: "1px solid #adc6ff",
            borderRadius: 6,
            fontSize: 12,
            color: "#1d39c4",
          }}
        >
          Session {sessionContext.sessionOrder + 1} of {sessionContext.totalSessions}
          {" · "}
          <b>{sessionContext.seriesName}</b>
          {" – "}
          {sessionContext.sessionType === "timedFinals"
            ? "Timed Finals"
            : sessionContext.sessionType === "prelims"
            ? "Prelims"
            : "Finals"}
        </div>
      )}
      {loading && <Skeleton />}
      {sd3LoadStep === "file-drop" &&
        mode === "entries" &&
        sessionContext?.sessionType === "finals" ? (
        <div
          style={{
            padding: "20px 16px",
            background: "#fff7e6",
            border: "1px solid #ffd591",
            borderRadius: 8,
            textAlign: "center",
          }}
        >
          <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
            Entry import is not available for Finals sessions
          </p>
          <p style={{ fontSize: 13, color: "#595959", margin: 0 }}>
            This session is a <b>Finals</b> session. Entries are seeded from the
            Prelims results — they cannot be imported directly.
            Use <b>Import Results</b> to import finals results into this session.
          </p>
        </div>
      ) : sd3LoadStep === "file-drop" && (
        <>
          {siblingSessions.length > 1 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>
                Import into session:
              </div>
              <Select
                style={{ width: "100%" }}
                value={effectiveMeetId}
                onChange={(val) => setEffectiveMeetId(val)}
                options={siblingSessions.map((s) => ({
                  value: s.meetId,
                  label: `${s.meetName} (${new Date(s.sessionDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })})`,
                }))}
              />
            </div>
          )}
          <FileDropZone
          setSd3Contents={setSd3Contents}
          setSd3LoadStep={setSd3LoadStep}
          setSd3Validation={setSd3Validation}
          mode={mode}
          onMultipleZipFiles={mode === "results" ? null : (zipFile, validFiles) => {
            setPendingZipFiles({ zipFile, validFiles })
            setSd3LoadStep("zip-file-picker")
          }}
          onHy3FileDrop={mode === "results" ? (contents) => {
            setRawHy3FileContents(contents)
            setSd3LoadStep("hy3-options")
          } : null}
        />
        </>
      )}
      {sd3LoadStep === "hy3-options" && (
        <Hy3OptionsPanel
          importScope={hy3ImportScope}
          setImportScope={setHy3ImportScope}
          matchOnEventNumbers={hy3MatchOnEventNumbers}
          setMatchOnEventNumbers={setHy3MatchOnEventNumbers}
          hasPrelimsFinals={hy3HasPrelimsFinals}
          scopeLocked={
            sessionContext?.sessionType === "prelims" ||
            sessionContext?.sessionType === "timedFinals" ||
            sessionContext?.sessionType === "finals"
          }
        />
      )}
      {sd3LoadStep === "session-map" && sessionRoundMapping && (
        <div>
          <p><b>Map file rounds to meet sessions</b></p>
          <p style={{ color: "#555", fontSize: 13 }}>
            This file contains both Prelims and Finals. Choose which existing session to import each round into, or skip it.
          </p>
          <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
            {(["prelims", "finals"] as const).map((round) => (
              <div key={round} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ width: 64, fontWeight: 600, textTransform: "capitalize", fontSize: 13 }}>
                  {round === "prelims" ? "Prelims" : "Finals"}
                </span>
                <Select
                  style={{ flex: 1 }}
                  value={sessionRoundMapping[round]}
                  onChange={(val) =>
                    setSessionRoundMapping((prev) => prev ? { ...prev, [round]: val } : prev)
                  }
                  options={[
                    ...siblingSessions.map((s) => ({
                      value: s.meetId,
                      label: `${s.meetName} (${new Date(s.sessionDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })})`,
                    })),
                    { value: "skip" as const, label: "Skip – don't import" },
                  ]}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {sd3LoadStep === "prelims-finals-detected" && (
        <div>
          <p>
            <b>Prelims + Finals Import</b>
          </p>
          <p>
            This file contains both <b>Prelims</b> and <b>Finals</b> results.
            Importing will:
          </p>
          <ul>
            <li>Create a new <b>“Prelims” companion meet</b> (copy of this meet)</li>
            <li>Import <b>prelims results</b> into the companion meet</li>
            <li>Import <b>finals results</b> into this meet</li>
            <li>Link both meets under a single <b>Multi-Session Meet</b></li>
          </ul>
          <p>Click <b>Next</b> to continue with club and swimmer matching.</p>
        </div>
      )}
      {sd3LoadStep === "file-options" && (
        <FileOptionsPanel
          matchOnEventNumbers={hy3MatchOnEventNumbers}
          setMatchOnEventNumbers={setHy3MatchOnEventNumbers}
        />
      )}
      {sd3LoadStep === "zip-file-picker" && pendingZipFiles && (
        <React.Fragment>
          <p>
            Your zip file contains multiple entries files. Select a file then
            click <b>Next</b>:
          </p>
          <div style={{ display: "grid", gap: 8 }}>
            {pendingZipFiles.validFiles.map((fileName) => {
              const ext = fileName.split(".").pop()?.toLowerCase() ?? ""
              const isHy3 = ext === "hy3"
              const isSelected = selectedZipFileName === fileName
              return (
                <button
                  key={fileName}
                  onClick={() => setSelectedZipFileName(fileName)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 14px",
                    border: isSelected
                      ? "2px solid #52c41a"
                      : isHy3
                      ? "2px solid #1677ff"
                      : "1px solid #d9d9d9",
                    borderRadius: 8,
                    background: isSelected
                      ? "#f6ffed"
                      : isHy3
                      ? "#e6f4ff"
                      : "#fafafa",
                    cursor: "pointer",
                    textAlign: "left",
                    width: "100%",
                    outline: "none",
                  }}
                >
                  <div>
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      {fileName}
                    </span>
                    {isSelected && (
                      <div
                        style={{ fontSize: 11, color: "#389e0d", marginTop: 2 }}
                      >
                        ✓ Selected
                      </div>
                    )}
                    {!isSelected && isHy3 && (
                      <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
                        HY3 format — best accuracy for exhibition &amp; alternate
                        designation flags
                      </div>
                    )}
                  </div>
                  {isSelected ? (
                    <span
                      style={{
                        background: "#52c41a",
                        color: "white",
                        borderRadius: 4,
                        padding: "2px 8px",
                        fontSize: 11,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        marginLeft: 8,
                      }}
                    >
                      ✓
                    </span>
                  ) : isHy3 ? (
                    <span
                      style={{
                        background: "#1677ff",
                        color: "white",
                        borderRadius: 4,
                        padding: "2px 8px",
                        fontSize: 11,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        marginLeft: 8,
                      }}
                    >
                      Recommended
                    </span>
                  ) : null}
                </button>
              )
            })}
          </div>
        </React.Fragment>
      )}
      {sd3LoadStep === "invalid-sd3" && (
        <React.Fragment>
          <p>
            <span
              style={{
                backgroundColor: "#f50",
                color: "white",
                padding: "3px",
                borderRadius: "3px",
              }}
            >
              There was a problem uploading your file:
            </span>
          </p>
          {sd3Validation.invalidBirthdaySwimmers.length > 0 && (
            <div>
              <p>
                The following swimmers in your import file{" "}
                <b>do not have birthdays</b>:
              </p>
              <ul>
                {sd3Validation.invalidBirthdaySwimmers.map(
                  (invalidSwimmer, idx) => {
                    return (
                      <li key={`invalid_swimmer_${idx}`}>
                        {invalidSwimmer.name} - file rows:{" "}
                        {invalidSwimmer.sd3Rows.join(", ")}
                      </li>
                    )
                  },
                )}
              </ul>
            </div>
          )}
        </React.Fragment>
      )}
      {sd3LoadStep === "fix-missing-dob" && sd3Contents && (() => {
        const swimmersNeedingDob = sd3Contents.swimmers.filter(
          (sw) =>
            (!sw.swimmerId || sw.swimmerId <= 0) &&
            (!sw.swimmerDOB || sw.swimmerDOB === "" || sw.swimmerDOB.startsWith("age-")),
        )
        return (
          <React.Fragment>
            <p>
              <span
                style={{
                  backgroundColor: "#fa8c16",
                  color: "white",
                  padding: "3px",
                  borderRadius: "3px",
                }}
              >
                Swimmers missing birthdays
              </span>
            </p>
            <p>
              The following swimmers have no date of birth in your import file.
              Enter a birthday for each one before continuing — swimmers without
              a birthday cannot be imported.
            </p>
            <div style={{ display: "grid", gap: 10 }}>
              {swimmersNeedingDob.map((sw, idx) => {
                const key = `${sw.club}|${sw.swimmerFirstName}|${sw.swimmerLastName}|${sw.swimmerGender}`
                const val = dobFixes[key] || ""
                const isValid = /^\d{4}-\d{2}-\d{2}$/.test(val)
                return (
                  <div
                    key={`dob_fix_${idx}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "6px 10px",
                      borderRadius: 6,
                      border: `1px solid ${isValid ? "#b7eb8f" : "#ffa39e"}`,
                      background: isValid ? "#f6ffed" : "#fff1f0",
                    }}
                  >
                    <span style={{ flex: 1, fontSize: 13 }}>
                      <b>{sw.swimmerFirstName} {sw.swimmerLastName}</b>
                      <span style={{ color: "#555", marginLeft: 8 }}>
                        {sw.swimmerGender}
                      </span>
                    </span>
                    <input
                      type="date"
                      value={val}
                      onChange={(e) =>
                        setDobFixes((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      style={{
                        padding: "3px 8px",
                        borderRadius: 4,
                        border: `1px solid ${isValid ? "#b7eb8f" : "#d9d9d9"}`,
                        fontSize: 13,
                      }}
                    />
                  </div>
                )
              })}
            </div>
          </React.Fragment>
        )
      })()}
      {sd3LoadStep === "no-event-numbers-warning" && (
        <React.Fragment>
          <p>
            <span
              style={{
                backgroundColor: "#fa8c16",
                color: "white",
                padding: "3px",
                borderRadius: "3px",
              }}
            >
              Missing event numbers
            </span>
          </p>
          <p>
            We noticed that this file does not contain event numbers. As a best
            practice, please include the event numbers to ensure accuracy. If
            you proceed with the import, we are going to attempt to reconcile
            the missing data, do you want to proceed?
          </p>
        </React.Fragment>
      )}
      {sd3LoadStep === "club-map" &&
        !hasNaNEventNumbers &&
        eventOrderErrors.length > 0 &&
        eventOrderErrors.map((err, idx) => <p key={`error_${idx}`}>{err}</p>)}
      {sd3LoadStep === "club-map" &&
        hasNaNEventNumbers &&
        eventOrderErrors.length > 0 && (
          <div style={{ marginBottom: "12px" }}>
            <p style={{ color: "#fa8c16", fontWeight: "bold" }}>
              Warning: The following events could not be matched and will be
              skipped:
            </p>
            {eventOrderErrors.map((err, idx) => (
              <p key={`error_${idx}`} style={{ color: "#fa8c16" }}>
                {err}
              </p>
            ))}
          </div>
        )}
      {sd3LoadStep === "club-map" &&
        sd3Contents && (
          <MapClubs
            clubMapping={clubMapping}
            setClubMapping={setClubMapping}
            clubNames={sd3Contents.clubNames}
            meetClubs={meetClubs}
          />
        )}
      {sd3LoadStep === "choose-swimmers" && sd3Contents && (
        <ChooseSwimmers
          meetId={effectiveMeetId}
          sd3Contents={sd3Contents}
          setSd3Contents={setSd3Contents}
          setSd3LoadStep={setSd3LoadStep}
          clubMapping={clubMapping}
          autoAdvanceStep={mode === "results" ? "load-data" : "entry-limits"}
          meetClubs={meetClubs}
          mode={mode}
        />
      )}
      {sd3LoadStep === "entry-limits" && sd3Contents && (
        <EntryLimitsStep
          sd3Contents={sd3Contents}
          clubMapping={clubMapping}
          individualEntryLimit={individualEntryLimit}
          relayEntryLimit={relayEntryLimit}
          totalEntryLimit={totalEntryLimit}
          deselectedEntryKeys={deselectedEntryKeys}
          relayLegReplacements={relayLegReplacements}
          onToggleIndividualEntry={(key) => {
            setDeselectedEntryKeys((prev) => {
              const next = { ...prev }
              if (next[key]) delete next[key]
              else next[key] = true
              return next
            })
          }}
          onToggleRelayLeg={(key) => {
            setDeselectedEntryKeys((prev) => {
              const next = { ...prev }
              if (next[key]) {
                // Re-checking: also clear any replacement
                delete next[key]
                setRelayLegReplacements((prevRep) => {
                  const nextRep = { ...prevRep }
                  delete nextRep[key]
                  return nextRep
                })
              } else {
                next[key] = true
              }
              return next
            })
          }}
          onOpenReplacementModal={(key) => setReplacementModalLegKey(key)}
        />
      )}
      {sd3LoadStep === "entry-limits" && sd3Contents && (
        <RelayReplacementModal
          legKey={replacementModalLegKey}
          sd3Contents={sd3Contents}
          clubMapping={clubMapping}
          deselectedEntryKeys={deselectedEntryKeys}
          relayLegReplacements={relayLegReplacements}
          individualEntryLimit={individualEntryLimit}
          relayEntryLimit={relayEntryLimit}
          totalEntryLimit={totalEntryLimit}
          onClose={() => setReplacementModalLegKey(null)}
          onConfirm={(legKey, replacement) => {
            if (replacement) {
              setRelayLegReplacements((prev) => ({ ...prev, [legKey]: replacement }))
            } else {
              setRelayLegReplacements((prev) => {
                const next = { ...prev }
                delete next[legKey]
                return next
              })
            }
            setReplacementModalLegKey(null)
          }}
        />
      )}
      {sd3LoadStep === "load-data" && !loading && sd3Contents && (
        <React.Fragment>
          {Object.keys(clubMapping).length > 0 ? (
            <p>
              <b>You are about to {mode === "results" ? "load results" : "load entries"}</b> for
              your swim meet. If you click "Ok", you will overwrite{" "}
              {mode === "results" ? "results" : "entries"} for all of the teams
              supplied in your upload file.
            </p>
          ) : (
            <p>You have chosen not to import any clubs!</p>
          )}
          {getSwimmersWhoWillNotBeUploaded().length > 0 && (
            <p>
              Additionally, <b>the following swimmers will not be uploaded</b>{" "}
              with your meet entries because they are not on the roster for
              their respective clubs.
            </p>
          )}
          {getSwimmersWhoWillNotBeUploaded().map((unMappedSwimmer, idx) => (
            <p
              key={`${unMappedSwimmer.swimmerFirstName}${unMappedSwimmer.swimmerLastName}_${idx}`}
            >
              {unMappedSwimmer.swimmerFirstName}{" "}
              {unMappedSwimmer.swimmerLastName} -{" "}
              {clubMapping[unMappedSwimmer.club].clubName}
            </p>
          ))}
        </React.Fragment>
      )}
      {sd3LoadStep === "summary" && summaryData && (
        <div style={{ display: "grid", gap: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#389e0d" }}>
            ✓ Import Complete
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
              gap: 12,
              padding: "12px 14px",
              borderRadius: 8,
              background: "#f6ffed",
              border: "1px solid #b7eb8f",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#237804" }}>
                {summaryData.totalIndividualInserted}
              </div>
              <div style={{ fontSize: 12, color: "#555" }}>Individual Entries</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#237804" }}>
                {summaryData.totalRelayTeamsInserted}
              </div>
              <div style={{ fontSize: 12, color: "#555" }}>Relay Teams</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#237804" }}>
                {summaryData.totalRelayLegsInserted}
              </div>
              <div style={{ fontSize: 12, color: "#555" }}>Relay Legs</div>
            </div>
            {summaryData.totalSkipped > 0 && (
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#cf1322" }}>
                  {summaryData.totalSkipped}
                </div>
                <div style={{ fontSize: 12, color: "#555" }}>Skipped</div>
              </div>
            )}
          </div>
          {(summaryData.totalExhibition > 0 ||
            summaryData.totalAlternateA > 0 ||
            summaryData.totalAlternateB > 0 ||
            summaryData.totalAlternateC > 0) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {summaryData.totalExhibition > 0 && (
                <span
                  style={{
                    background: "#e6f7ff",
                    border: "1px solid #91d5ff",
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 12,
                  }}
                >
                  Exhibition: {summaryData.totalExhibition}
                </span>
              )}
              {summaryData.totalAlternateA > 0 && (
                <span
                  style={{
                    background: "#fff7e6",
                    border: "1px solid #ffd591",
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 12,
                  }}
                >
                  Alt A: {summaryData.totalAlternateA}
                </span>
              )}
              {summaryData.totalAlternateB > 0 && (
                <span
                  style={{
                    background: "#fff7e6",
                    border: "1px solid #ffd591",
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 12,
                  }}
                >
                  Alt B: {summaryData.totalAlternateB}
                </span>
              )}
              {summaryData.totalAlternateC > 0 && (
                <span
                  style={{
                    background: "#fff7e6",
                    border: "1px solid #ffd591",
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 12,
                  }}
                >
                  Alt C: {summaryData.totalAlternateC}
                </span>
              )}
            </div>
          )}
          {summaryData.clubs.map((club, idx) => (
            <div
              key={idx}
              style={{
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 8,
                padding: "10px 14px",
                display: "grid",
                gap: 8,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 14 }}>{club.clubName}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: 13 }}>
                <span>Individual: <b>{club.individualInserted}</b></span>
                <span>Relay Teams: <b>{club.relayTeamsInserted}</b></span>
                <span>Relay Legs: <b>{club.relayLegsInserted}</b></span>
                {club.exhibitionCount > 0 && (
                  <span style={{ color: "#0958d9" }}>
                    Exhibition: <b>{club.exhibitionCount}</b>
                  </span>
                )}
                {club.alternateACount > 0 && (
                  <span style={{ color: "#d46b08" }}>
                    Alt A: <b>{club.alternateACount}</b>
                  </span>
                )}
                {club.alternateBCount > 0 && (
                  <span style={{ color: "#d46b08" }}>
                    Alt B: <b>{club.alternateBCount}</b>
                  </span>
                )}
                {club.alternateCCount > 0 && (
                  <span style={{ color: "#d46b08" }}>
                    Alt C: <b>{club.alternateCCount}</b>
                  </span>
                )}
              </div>
              {club.skippedEntries.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#cf1322",
                      marginBottom: 4,
                    }}
                  >
                    Skipped ({club.skippedEntries.length}):
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gap: 4,
                      maxHeight: 160,
                      overflowY: "auto",
                    }}
                  >
                    {club.skippedEntries.map((skipped, sIdx) => (
                      <div
                        key={sIdx}
                        style={{
                          fontSize: 12,
                          padding: "3px 8px",
                          background: "#fff1f0",
                          borderRadius: 4,
                          border: "1px solid #ffa39e",
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>{skipped.swimmerName}</span>
                        {skipped.eventDescription && (
                          <span style={{ color: "#555" }}>
                            {" "}— {skipped.eventDescription}
                          </span>
                        )}
                        <div style={{ color: "#cf1322" }}>{skipped.reason}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}

// get clubs in meet
// parse the sd3
